import { AdvanceDeduction, ExternalAdvance, ServiceItem, ServiceTransaction } from '../types';
import {
    centsFromEuro,
    centsFromEuroInput,
    euroFromCents,
    parseDecimalInput,
    roundCents,
    splitGross,
    taxRateBpFromPercent,
} from './money';
// Aus dem blattlosen Modul, NICHT aus projectLv: projectLv liest seinerseits
// altCoveredQuantities von hier, ein Import von dort schloesse einen Zyklus.
import { isHourlyUnit } from './serviceUnits';
// serviceLogUtils ist ein Blattmodul mit ausschliesslich Typ-Importen — es
// kann keinen Zyklus schliessen.
import { buchungstag, isBackfilledAltEntry, isPostingEntry } from './serviceLogUtils';
// projectSplit ist ebenfalls blattnah (types + serviceLogUtils) und kennt
// dieses Modul nicht — kein Zyklus.
import { altCoveredByParty, billedFor } from './projectSplit';

/**
 * Externe Alt-Abschlaege (Bestandsprojekte) als Abzugsposten der kumulativen
 * Abrechnung.
 *
 * Pur: keine Datenbank, kein Date.now(). Die CRUD-Zugriffe auf
 * public.external_advances liegen in invoiceService (fetchExternalAdvances
 * u. a.), damit dieses Modul ohne jsdom testbar bleibt.
 */

/**
 * Quell-Praefix externer Abzuege in AdvanceDeduction.advanceInvoiceId.
 *
 * Traegt die Unterscheidung DURCH die bestehende Abzugsmechanik: Gruppierung,
 * Summen und PDF behandeln externe Eintraege wie jeden Abzug, aber jede
 * Stelle, die advanceInvoiceId gegen public.invoices haelt (Statuspruefung,
 * DEDUCTS_ADVANCE-Verknuepfung, Abloesung), MUSS vorher mit
 * isExternalAdvanceId filtern — ein 'ext:…' ist keine uuid und kein Beleg.
 */
export const EXTERNAL_ADVANCE_ID_PREFIX = 'ext:';

export const isExternalAdvanceId = (id: string): boolean =>
    id.startsWith(EXTERNAL_ADVANCE_ID_PREFIX);

/**
 * Alt-Abschlaege in die Abzugsform der kumulativen Rechnung.
 *
 * Netto/Steuer aus Brutto ueber splitGross: die Steuer ist der Rest, nicht
 * eine zweite Rundung — netCents + taxCents === grossCents gilt immer
 * (Zahlungsaufteilung und DATEV verlangen das auch fuer Abzugszeilen).
 * Sortierung wie deductionsFromPriorInvoices: Datum, dann Nummer; ein
 * fehlendes Datum ('') steht stabil vorn.
 */
export const deductionsFromExternalAdvances = (
    rows: ExternalAdvance[],
): AdvanceDeduction[] =>
    rows
        .map((row) => {
            const { netCents, taxCents } = splitGross(row.grossCents, row.taxRateBp);
            return {
                advanceInvoiceId: `${EXTERNAL_ADVANCE_ID_PREFIX}${row.id}`,
                invoiceNumber: row.invoiceNumber,
                invoiceDate: row.invoiceDate ?? '',
                taxRateBp: row.taxRateBp,
                netCents,
                taxCents,
                grossCents: row.grossCents,
            };
        })
        .sort(
            (a, b) =>
                a.invoiceDate.localeCompare(b.invoiceDate) ||
                a.invoiceNumber.localeCompare(b.invoiceNumber),
        );

// ═══════════════════════════════════════════════════════════════════════════
// Abgleich mit der eingelesenen letzten Alt-Rechnung
// ═══════════════════════════════════════════════════════════════════════════

export interface ExistingAdvanceRef {
    invoiceNumber: string;
    grossCents: number;
    source: 'app' | 'external';
}

/** Aus dem PDF der letzten Alt-Rechnung extrahierter Abschlag (Gemini). */
export interface ExtractedAdvance {
    invoiceNumber: string;
    invoiceDate?: string;
    grossCents: number;
    taxRateBp: number;
}

export interface ReconciledAdvance {
    extracted: ExtractedAdvance;
    /**
     * matched  — Nummer und Betrag stimmen mit einem hinterlegten Eintrag
     *            ueberein; nichts zu tun.
     * new      — Nummer unbekannt; Kandidat fuer die Uebernahme.
     * mismatch — Nummer bekannt, Betrag weicht ab; der Nutzer entscheidet.
     */
    status: 'matched' | 'new' | 'mismatch';
    existing?: ExistingAdvanceRef;
}

/** Nummern-Normalisierung fuer den Abgleich: Leerraum und Schreibung egal. */
const normNumber = (n: string): string => n.trim().toLowerCase();

/**
 * Kollidiert eine nachzutragende Belegnummer mit einer bestehenden Quelle?
 *
 * Der DB-Unique-Index deckt nur external_advances ab — eine Nummer, die
 * bereits als APP-Beleg des Projekts existiert, wuerde sonst doppelt
 * abgezogen: einmal automatisch (deductionsFromPriorInvoices) und einmal
 * als externer Eintrag. Beim Bearbeiten schliesst excludeExternalId den
 * eigenen Eintrag aus.
 */
export const externalAdvanceNumberConflict = (
    invoiceNumber: string,
    appInvoiceNumbers: string[],
    externals: ExternalAdvance[],
    excludeExternalId?: string,
): 'app' | 'external' | null => {
    const key = normNumber(invoiceNumber);
    if (!key) return null;
    if (appInvoiceNumbers.some((n) => normNumber(n) === key)) return 'app';
    if (externals.some((e) => e.id !== excludeExternalId && normNumber(e.invoiceNumber) === key)) {
        return 'external';
    }
    return null;
};

/**
 * Hinterlegte Vorrechnungen (App + Extern) je Belegnummer.
 *
 * App-Abzuege werden je Nummer summiert: ein Beleg ueber mehrere Steuersaetze
 * steht mit mehreren Abzugszeilen in der Liste, ist aber EIN Beleg — der
 * Abgleich vergleicht Belege, nicht Steuerzeilen.
 */
export const aggregateExistingAdvances = (
    appDeductions: AdvanceDeduction[],
    externals: ExternalAdvance[],
): ExistingAdvanceRef[] => {
    const out: ExistingAdvanceRef[] = [];
    const appByNumber = new Map<string, ExistingAdvanceRef>();
    for (const d of appDeductions) {
        const key = normNumber(d.invoiceNumber);
        const found = appByNumber.get(key);
        if (found) {
            found.grossCents += d.grossCents;
        } else {
            const ref: ExistingAdvanceRef = {
                invoiceNumber: d.invoiceNumber,
                grossCents: d.grossCents,
                source: 'app',
            };
            appByNumber.set(key, ref);
            out.push(ref);
        }
    }
    for (const e of externals) {
        out.push({ invoiceNumber: e.invoiceNumber, grossCents: e.grossCents, source: 'external' });
    }
    return out;
};

/**
 * Extrahierte Abschlaege gegen die hinterlegten abgleichen.
 *
 * Match ueber die Belegnummer, Betragsvergleich exakt in Cent: eine Abweichung
 * ist eine Entscheidung des Nutzers, keine Toleranzfrage — der hinterlegte
 * Betrag geht in den Abzug der naechsten Rechnung ein.
 */
/**
 * Gemini-Ergebnis von "Letzte Rechnung einlesen" in Abgleichszeilen.
 *
 * Die letzte Alt-Rechnung ist SELBST ein nachzutragender Abschlag; ihr
 * Abzugsblock nennt alle davor. Nichts wird geraten: Zeilen ohne Nummer oder
 * ohne positiven Bruttobetrag entfallen, ein unlesbares Datum wird verworfen
 * statt erfunden. Betraege kommen als EUR (Zahl oder deutscher String) und
 * werden nach Cent gewandelt.
 */
export const extractedAdvancesFromAi = (
    parsed: unknown,
    defaultTaxRateBp: number,
): ExtractedAdvance[] => {
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
    const doc = parsed as { rechnung?: unknown; abschlaege?: unknown };

    const toGrossCents = (v: unknown): number | null => {
        if (typeof v === 'number' && Number.isFinite(v)) return centsFromEuro(v);
        if (typeof v === 'string') return centsFromEuroInput(v);
        return null;
    };
    const toIsoDate = (v: unknown): string | undefined =>
        typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined;

    const rechnung =
        doc.rechnung && typeof doc.rechnung === 'object' ? (doc.rechnung as any) : undefined;
    const toRateBp = (v: unknown): number | null => {
        const percent = Number(v);
        return Number.isFinite(percent) && percent > 0 ? taxRateBpFromPercent(percent) : null;
    };
    // Rueckfallkette je Zeile: eigener Satz -> Satz der letzten Rechnung ->
    // Konfigurationsstandard. Altprojekte ueberspannen Satzwechsel (19 -> 16 %
    // in 2020) oder mischen 7 %-Leistungen — der Satz der LETZTEN Rechnung
    // gilt dann gerade nicht fuer alle Vorrechnungen.
    const fallbackRateBp = toRateBp(rechnung?.steuersatz) ?? defaultTaxRateBp;

    const toRow = (raw: any): ExtractedAdvance | null => {
        if (!raw || typeof raw !== 'object') return null;
        const invoiceNumber = String(raw.nummer ?? '').trim();
        const grossCents = toGrossCents(raw.brutto);
        if (!invoiceNumber || grossCents == null || grossCents <= 0) return null;
        return {
            invoiceNumber,
            invoiceDate: toIsoDate(raw.datum),
            grossCents,
            taxRateBp: toRateBp(raw.steuersatz) ?? fallbackRateBp,
        };
    };

    const abschlaege = Array.isArray(doc.abschlaege) ? doc.abschlaege : [];
    return [...abschlaege.map(toRow), toRow(rechnung)].filter(
        (r): r is ExtractedAdvance => r !== null,
    );
};

export const reconcileAdvances = (
    extracted: ExtractedAdvance[],
    existing: ExistingAdvanceRef[],
): ReconciledAdvance[] => {
    const byNumber = new Map<string, ExistingAdvanceRef>(
        existing.map((e) => [normNumber(e.invoiceNumber), e]),
    );
    return extracted.map((x) => {
        const found = byNumber.get(normNumber(x.invoiceNumber));
        if (!found) return { extracted: x, status: 'new' };
        return {
            extracted: x,
            status: found.grossCents === x.grossCents ? 'matched' : 'mismatch',
            existing: found,
        };
    });
};

// ═══════════════════════════════════════════════════════════════════════════
// Positionen der Alt-Rechnung nachtragen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Warum ueberhaupt: die Alt-Abschlaege werden abgezogen, aber die LEISTUNG,
 * die sie gefordert haben, steht nirgends im Projekt. Die kumulative Rechnung
 * prueft den Abzug gegen die Bemessungsgrundlage
 * (advanceInvoiceLogic: advance_exceeds_rate_basis / payable_negative) und
 * blockiert — zu Recht, denn ohne die Alt-Positionen zieht der Beleg mehr ab,
 * als er ausweist. Nachgetragen wird deshalb Menge UND Leistungsstand:
 * beauftragt = erledigt, damit der kumulative Stand aufgeht.
 */

/** Eine aus dem PDF gelesene Leistungsposition der Alt-Rechnung. */
export interface ExtractedPosition {
    /** LV-/Ordnungsnummer wie im Dokument; '' wenn keine ausgewiesen ist. */
    lvPosition: string;
    title: string;
    /** Die ABGERECHNETE Menge — bei einer Teilleistung nur deren Anteil. */
    quantity: number;
    unit: string;
    /** Netto-Einzelpreis in Cent — der Preis der VOLLEN Einheit. */
    unitPriceCents: number;
    /** Netto-Zeilensumme in Cent: der Betrag, den der Beleg fordert. */
    totalCents: number;
    /**
     * Die beauftragte Gesamtmenge, wenn der Beleg nur einen TEIL davon
     * abrechnet ("SiGe-Plan, 50 %"). Fehlt das Feld, deckt die Zeile die
     * ganze Position ab und beauftragt = abgerechnet.
     *
     * Warum getrennt: ohne diese Groesse landete eine 50-%-Zeile als
     * vollstaendig erledigte Position zum halben Preis im Projekt. Der Betrag
     * stimmte, aber die zweite Haelfte war unsichtbar — sie liesse sich nie
     * mehr abrechnen, weil die Position als fertig gilt.
     */
    contractedQuantity?: number;
    /** Abgerechneter Anteil in Prozent, wenn der Beleg einen ausweist. */
    sharePercent?: number;
}

/**
 * Zeilen, die keine Leistung sind.
 *
 * Der Rechnungsblock einer kumulativen Abschlagsrechnung enthaelt zwischen den
 * Positionen Summen-, Uebertrags- und Abzugszeilen. Als Position uebernommen
 * wuerden sie den Leistungsstand verdoppeln — genau das, was der Nachtrag
 * verhindern soll. Zweite Schranke: ein negativer Betrag ist nie eine
 * erbrachte Leistung.
 */
const NON_SERVICE_PATTERNS = [
    'abschlag',
    'abzueglich',
    'abzüglich',
    'zwischensumme',
    'uebertrag',
    'übertrag',
    'summe',
    'bereits gefordert',
    'skonto',
    'nachlass',
    'umsatzsteuer',
    'mehrwertsteuer',
];

export const isNonServiceLine = (title: string): boolean => {
    const t = title.trim().toLowerCase();
    if (!t) return true;
    return NON_SERVICE_PATTERNS.some((p) => t.includes(p));
};

/**
 * Wie weit die rohe Division von einer ganzen Zahl abweichen darf, damit sie
 * als diese ganze Zahl gilt. Gerechnet wird in Cent, echte Rundungsreste
 * liegen also weit unterhalb.
 */
const WHOLE_QUANTITY_TOLERANCE = 0.005;

/** Cent-Toleranz der Rueckprobe einer auf zwei Stellen gerundeten Menge. */
const DERIVED_QUANTITY_CENT_TOLERANCE = 1;

/**
 * Menge aus Zeilensumme und Einzelpreis — fuer Belege OHNE Mengenspalte.
 *
 * Nicht jede Rechnung fuehrt die Menge in einer eigenen Spalte. Verbreitet ist
 * die Bauform, in der nur "Leistungen | netto | MwSt | brutto" steht und die
 * Menge im Leistungstext mitlaeuft ("57 * 160,00 € / Begehung", "57,00 Stunden
 * * 120,00 €"). Die KI liefert dann zuverlaessig die Betraege, aber keine
 * Menge — und die Zeile fiel bisher ersatzlos heraus. Der Leistungsstand blieb
 * um genau diese Position zu niedrig, bis die naechste kumulative Rechnung
 * daran blockierte (Projekt 5956: 57,00 Stunden auf der Alt-Rechnung, 23,00 im
 * Projekt).
 *
 * ABGELEITET, NICHT GERATEN: die Menge muss sich aus den beiden Betraegen
 * zurueckrechnen lassen, sonst gibt es keine. Zwei Faelle werden anerkannt:
 *  - Die Division ist praktisch ganzzahlig (684000 / 12000 = 57) — der
 *    Regelfall, und der eindeutigste.
 *  - Sie ergibt zwei Nachkommastellen, die MULTIPLIZIERT wieder die
 *    Zeilensumme treffen (1,75 Std., 10,25 Std.).
 * Alles andere liefert null: eine Menge, die sich nicht zurueckrechnen laesst,
 * traegt einen Betrag ins LV, den der Beleg nicht deckt.
 */
export const derivedQuantity = (
    totalCents: number,
    unitPriceCents: number,
): number | null => {
    if (!(totalCents > 0) || !(unitPriceCents > 0)) return null;
    const raw = totalCents / unitPriceCents;
    if (!Number.isFinite(raw) || raw <= 0) return null;

    const whole = Math.round(raw);
    if (whole > 0 && Math.abs(raw - whole) <= WHOLE_QUANTITY_TOLERANCE) return whole;

    const twoDecimals = Math.round(raw * 100) / 100;
    if (!(twoDecimals > 0)) return null;
    const check = Math.abs(roundCents(twoDecimals * unitPriceCents) - totalCents);
    return check <= DERIVED_QUANTITY_CENT_TOLERANCE ? twoDecimals : null;
};

/**
 * Eine Belegzeile, die die Erkennung NICHT uebernehmen konnte.
 *
 * `nonService` ist der gewollte Fall (Summen-, Uebertrags-, Abzugszeilen) und
 * gehoert nicht vor den Nutzer. `noAmount` dagegen schon: dort stand eine
 * Leistung im Beleg, die im Projekt nicht ankommt — und stillschweigend
 * verschwand sie bisher zwischen `continue` und einer leeren Tabelle.
 */
export interface SkippedPositionRow {
    title: string;
    reason: 'nonService' | 'noAmount' | 'inconsistent';
}

/**
 * Gemini-Feld "positionen" in Leistungszeilen — samt dem, was dabei liegen
 * blieb.
 *
 * Tolerant gegenueber der ALTEN Edge-Function: fehlt das Feld, ist das
 * Ergebnis leer und der Abschlagsteil des Imports laeuft unveraendert weiter.
 * Nichts wird geraten — eine Zeile ohne Bezeichnung und ohne jeden Betrag
 * entfaellt, statt mit 0 angelegt zu werden: eine Position mit Preis 0 sieht
 * im LV aus wie eine geschenkte Leistung.
 */
/**
 * Mengen-/Preisangabe am Ende einer Bezeichnung: "; 42,5 Stunden * 70,00 €".
 *
 * Belege setzen die Angabe oft auf eine eigene Zeile unter den Titel, und die
 * Erkennung liest beide Zeilen als EINE Bezeichnung. Der Titel wandert damit
 * unveraendert ins LV — in Projekt 5940 hiess die Position danach
 * "Organisation und Durchfuehrung von Sicherheitsbegehungen inkl.
 * Berichterstellung; 42,5 Stunden * 70,00 EUR".
 *
 * Das ist nicht nur haesslich: Menge und Preis stehen in eigenen Feldern, im
 * Titel sind sie eine zweite Wahrheit, die beim naechsten Nachtrag falsch
 * dasteht — und sie verzerrt jeden spaeteren Titelabgleich.
 */
const QUANTITY_PRICE_TAIL =
    /[;,]?\s*\d[\d.,]*\s*(?:std\.?|stunden|stk\.?|st(?:ü|ue)ck|einheiten?|pauschal)?\s*[*x×]\s*\d[\d.,]*\s*(?:€|eur)?\s*$/i;
/** Satzzeichen, die nach dem Abschneiden allein am Ende zurueckbleiben. */
const TRAILING_PUNCTUATION = /[\s;,:·–-]+$/;

/**
 * Bezeichnung einer Belegzeile fuer das LV: ohne angehaengte Mengen-/
 * Preisangabe und ohne verwaistes Satzzeichen.
 *
 * Bewusst konservativ: geschnitten wird nur am ENDE und nur, wenn dort
 * tatsaechlich "Zahl * Betrag" steht. Bleibt nichts uebrig, gilt der
 * Originaltitel — lieber unschoen als leer.
 */
export const cleanPositionTitle = (raw: string): string => {
    const stripped = (raw || '').trim().replace(QUANTITY_PRICE_TAIL, '');
    const trimmed = stripped.replace(TRAILING_PUNCTUATION, '').trim();
    return trimmed || (raw || '').trim();
};

export const extractPositionRows = (
    parsed: unknown,
): { positions: ExtractedPosition[]; skipped: SkippedPositionRow[] } => {
    const empty = { positions: [] as ExtractedPosition[], skipped: [] as SkippedPositionRow[] };
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return empty;
    const rows = (parsed as { positionen?: unknown }).positionen;
    if (!Array.isArray(rows)) return empty;

    const toCents = (v: unknown): number | null => {
        if (typeof v === 'number' && Number.isFinite(v)) return centsFromEuro(v);
        if (typeof v === 'string' && v.trim()) return centsFromEuroInput(v);
        return null;
    };
    // Menge ueber denselben Schreibweisen-Parser wie die Betraege. Ein eigener
    // "Punkte raus, Komma zu Punkt" machte aus der englischen Schreibweise
    // "1.5" die Menge 15 — die zehnfache Leistung, direkt in der Rechnung.
    const toQuantity = (v: unknown): number | null => {
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        if (typeof v === 'string' && v.trim()) return parseDecimalInput(v);
        return null;
    };
    /**
     * Der abgerechnete Anteil — nur ein ECHTER Teil zaehlt.
     *
     * 100 % ist die volle Leistung und damit kein Sonderfall; 0 oder daneben
     * heisst "nicht ausgewiesen". Beides faellt auf den normalen Weg zurueck,
     * statt eine Teilleistung zu erfinden.
     */
    const toSharePercent = (v: unknown): number | null => {
        const raw = toQuantity(v);
        return raw != null && raw > 0 && raw < 100 ? raw : null;
    };

    const out: ExtractedPosition[] = [];
    const skipped: SkippedPositionRow[] = [];
    for (const raw of rows) {
        if (!raw || typeof raw !== 'object') continue;
        const r = raw as Record<string, unknown>;
        const title = cleanPositionTitle(String(r.bezeichnung ?? ''));
        if (isNonServiceLine(title)) {
            skipped.push({ title, reason: 'nonService' });
            continue;
        }

        const unitPrice = toCents(r.einzelpreis);
        const total = toCents(r.gesamtpreis);

        // Menge aus dem Beleg — und nur, wenn dort keine steht, aus den
        // Betraegen zurueckgerechnet (siehe derivedQuantity). Die Reihenfolge
        // ist tragend: eine ausgewiesene Menge hat immer Vorrang, die
        // Ableitung ist der Rueckfall fuer Belege ohne Mengenspalte.
        let quantity = toQuantity(r.menge);
        if (quantity == null || quantity <= 0) {
            quantity =
                unitPrice != null && total != null ? derivedQuantity(total, unitPrice) : null;
        }

        // Letzter Rueckfall: die PAUSCHALZEILE.
        //
        // Teilleistungsrechnungen fuehren ihre Leistungspunkte oft ganz ohne
        // Mengen- und Einzelpreisspalte — "1. Vorankündigung … 250,00 €",
        // "2. SiGe-Plan … 890,00 €". Dann steht genau ein Betrag da, und beide
        // Wege oben liefern nichts: die Zeile fiel ersatzlos heraus, und die
        // Leistung fehlte anschliessend im Projekt (Projekt 6193).
        //
        // Menge 1 ist hier NICHT geraten, sondern die einzige Lesart, die der
        // Beleg zulaesst: 1 × Betrag ergibt exakt den Betrag, der auf der
        // Rechnung steht. Der uebernommene WERT stimmt damit in jedem Fall,
        // offen bleibt nur die Stueckelung — und die stand im Beleg auch nicht.
        // Greift bewusst erst nach derivedQuantity: wo sich eine echte Menge
        // zurueckrechnen laesst ("57 × 120,00"), hat sie Vorrang.
        //
        // Der Rueckfall greift NUR, wenn der Beleg genau eine Betragsangabe
        // fuehrt. Stehen Einzelpreis UND Zeilensumme da und die Menge liess
        // sich trotzdem nicht zurueckrechnen (700,00 gegen 10.000,00), dann
        // widersprechen die beiden einander — Menge 1 waere dort keine Lesart,
        // sondern eine Erfindung, und sie verkuerzte die Zeile auf den
        // Einzelpreis. Solche Zeilen bleiben draussen und werden gemeldet.
        const hasBothAmounts = unitPrice != null && unitPrice > 0 && total != null && total > 0;
        if (quantity == null || quantity <= 0) {
            const amount = total ?? unitPrice;
            if (!hasBothAmounts && amount != null && amount > 0) quantity = 1;
        }
        if (quantity == null || quantity <= 0) {
            skipped.push({ title, reason: hasBothAmounts ? 'inconsistent' : 'noAmount' });
            continue;
        }

        // Eine der beiden Groessen genuegt; die andere folgt. Preise unter 0
        // sind Gutschriften/Abzuege und gehoeren nicht ins LV.
        let unitPriceCents: number;
        let totalCents: number;
        if (unitPrice != null && unitPrice > 0) {
            unitPriceCents = unitPrice;
            totalCents = total != null && total > 0 ? total : roundCents(unitPrice * quantity);
        } else if (total != null && total > 0) {
            totalCents = total;
            unitPriceCents = roundCents(total / quantity);
        } else {
            skipped.push({ title, reason: 'noAmount' });
            continue;
        }

        // ── Teilleistung ("SiGe-Plan, 50 %") ───────────────────────────
        //
        // Bis hierher ist `quantity` die VOLLE Menge der Position (der Prompt
        // verlangt "menge" so) und `totalCents` der Betrag, den der Beleg
        // fordert — also der TEILbetrag. Beides zusammen ergibt sonst einen
        // falschen Einzelpreis: 1 × 445,00 statt 0,5 × 890,00.
        //
        // Gerechnet, nicht geraten: der volle Zeilenwert ist Teilbetrag /
        // Anteil, der Einzelpreis dieser Wert je beauftragter Einheit. Der
        // geforderte Betrag bleibt unangetastet — er ist die einzige Groesse,
        // die schwarz auf weiss auf der Rechnung steht.
        const sharePercent = toSharePercent(r.prozent);
        let contractedQuantity: number | undefined;
        if (sharePercent != null) {
            const share = sharePercent / 100;
            const billedQuantity = quantity * share;
            if (billedQuantity > 0) {
                contractedQuantity = quantity;
                unitPriceCents = roundCents(totalCents / billedQuantity);
                quantity = billedQuantity;
            }
        }

        out.push({
            lvPosition: String(r.lvNummer ?? '').trim(),
            title,
            quantity,
            unit: String(r.einheit ?? '').trim(),
            unitPriceCents,
            totalCents,
            ...(contractedQuantity != null ? { contractedQuantity } : {}),
            ...(contractedQuantity != null ? { sharePercent: sharePercent as number } : {}),
        });
    }
    return { positions: out, skipped };
};

/** Nur die uebernehmbaren Zeilen — der bisherige, unveraenderte Aufruf. */
export const extractedPositionsFromAi = (parsed: unknown): ExtractedPosition[] =>
    extractPositionRows(parsed).positions;

/** Belegkopf der eingelesenen Alt-Rechnung, soweit im Dokument vorhanden. */
export interface ExtractedInvoiceMeta {
    invoiceNumber: string;
    /** ISO YYYY-MM-DD; undefined, wenn unlesbar — nicht erfunden. */
    invoiceDate?: string;
    /** Nettosumme der Gesamtleistung in Cent; null, wenn nicht ausgewiesen. */
    netTotalCents: number | null;
}

/**
 * Belegkopf fuer Vorbelegung und Gegenrechnung.
 *
 * `gesamtnetto` liefert erst die aktualisierte Edge-Function; fehlt es, faellt
 * die Gegenrechnung ersatzlos aus (null) statt gegen `brutto` zu rechnen —
 * das ist bei einer kumulativen Rechnung der ZAHLBETRAG nach Abzug und waere
 * als Vergleichsgroesse schlicht falsch.
 */
export const extractedInvoiceMetaFromAi = (parsed: unknown): ExtractedInvoiceMeta => {
    const empty: ExtractedInvoiceMeta = { invoiceNumber: '', netTotalCents: null };
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return empty;
    const r = (parsed as { rechnung?: unknown }).rechnung;
    if (!r || typeof r !== 'object') return empty;
    const doc = r as Record<string, unknown>;
    const net = Number(doc.gesamtnetto);
    const dateRaw = doc.datum;
    return {
        invoiceNumber: String(doc.nummer ?? '').trim(),
        invoiceDate:
            typeof dateRaw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateRaw) ? dateRaw : undefined,
        netTotalCents: Number.isFinite(net) && net > 0 ? centsFromEuro(net) : null,
    };
};

/**
 * matched        — Position ist im Projekt vorhanden, Preis passt; nichts zu tun.
 * new            — Position fehlt im Projekt; Kandidat fuer die Uebernahme.
 * price_mismatch — Position vorhanden, aber der Einzelpreis weicht ab.
 * suggested      — kein exakter Treffer, aber aehnliche Positionen im LV; der
 *                  Nutzer ordnet zu oder legt bewusst neu an.
 */
export type PositionMatchStatus = 'matched' | 'new' | 'price_mismatch' | 'suggested';

export interface ExistingPositionRef {
    id: string;
    lvPosition: string;
    title: string;
    unit: string;
    /** Einzelpreis des LV in Cent. */
    unitPriceCents: number;
}

export interface ReconciledPosition {
    extracted: ExtractedPosition;
    status: PositionMatchStatus;
    existing?: ExistingPositionRef;
    /**
     * Nur bei 'suggested': aehnliche Positionen des Projekts, beste zuerst.
     * Vorschlag, keine Zuordnung — entschieden wird in der Oberflaeche.
     */
    candidates?: PositionCandidate[];
}

/** Titel-Normalisierung: Leerraum, Mehrfach-Leerzeichen und Schreibung egal. */
const normTitle = (t: string): string => t.trim().toLowerCase().replace(/\s+/g, ' ');

// ── Aehnlichkeitssuche fuer Positionen ohne exakten Treffer ────────────────

/**
 * Warum es die Suche gibt: Alt-Rechnungen fuehren ihre Positionen ohne
 * LV-Nummer und in der Sprache des Belegs — "Honorar für die
 * Baustellenbegehungen" ist im Projekt "Sicherheitsbegehung durchführen
 * (turnusmäßig)". Der exakte Abgleich findet das nicht, meldet 'new' und der
 * Nachtrag legte eine zweite, inhaltsgleiche Position an: der Leistungsstand
 * verdoppelt sich, und beide Zeilen stehen danach als "offen" in der
 * Abrechnung.
 *
 * Rein rechnerisch und ohne KI: dieselbe Eingabe ergibt immer dieselben
 * Vorschlaege, und ein Vorschlag ist nie eine Zuordnung — die trifft der
 * Nutzer.
 */

/**
 * Woerter ohne Unterscheidungskraft in einem LV-Titel.
 *
 * "Honorar", "für" und "erstellen" stehen in jedem zweiten Titel; blieben sie
 * drin, ueberdeckte ihre Uebereinstimmung die eigentliche Leistung.
 */
const TITLE_FILLER_WORDS = new Set([
    'honorar', 'honorare', 'pauschal', 'pauschale', 'kosten', 'leistung', 'leistungen',
    'fuer', 'der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einen', 'einer',
    'und', 'oder', 'von', 'vom', 'zur', 'zum', 'pro', 'per', 'inkl', 'inklusive',
    'sowie', 'nach', 'gemaess', 'erstellen', 'erstellung', 'durchfuehren',
    'durchfuehrung', 'position', 'pos',
]);

/** Umlaute falten: der Beleg schreibt "gemäß", das LV "gemaess". */
const foldUmlauts = (s: string): string =>
    s
        .replace(/[äÄ]/g, 'ae')
        .replace(/[öÖ]/g, 'oe')
        .replace(/[üÜ]/g, 'ue')
        .replace(/ß/g, 'ss');

/**
 * Flexionsendungen abschneiden, aber nur wenn genug Wort uebrig bleibt:
 * "Begehungen" und "Begehung" sind dieselbe Leistung, "Plan" und "Pla" nicht.
 */
const STEM_SUFFIXES = ['en', 'er', 'es', 'e', 'n', 's'];
const MIN_STEM_LENGTH = 4;

const stemOf = (word: string): string => {
    for (const suffix of STEM_SUFFIXES) {
        if (word.endsWith(suffix) && word.length - suffix.length >= MIN_STEM_LENGTH) {
            return word.slice(0, -suffix.length);
        }
    }
    return word;
};

/**
 * Ein Wort in beiden Formen.
 *
 * Verglichen wird gegen BEIDE: das Stemmen schneidet Endungen ab, und genau das
 * zerstoert sonst die Bruecke ueber Komposita — aus "Gesundheitsschutzplan"
 * wird "…schutzpla", und "Plan" steckt darin nicht mehr. Die ungestemmte Form
 * haelt diesen Bezug, die gestemmte den zwischen "Begehungen" und "Begehung".
 */
interface StemForm {
    word: string;
    stem: string;
}

const titleStemForms = (title: string): StemForm[] => {
    const out: StemForm[] = [];
    for (const word of foldUmlauts(String(title || '').toLowerCase())
        .replace(/[^a-z0-9]+/g, ' ')
        .split(' ')) {
        if (word.length < 3 || TITLE_FILLER_WORDS.has(word)) continue;
        const stem = stemOf(word);
        if (!out.some((f) => f.stem === stem)) out.push({ word, stem });
    }
    return out;
};

/**
 * Titel in vergleichbare Wortstaemme.
 *
 * Kleinschreibung, Umlaute gefaltet, Satzzeichen als Trenner (aus "SiGe-Plan"
 * werden "sige" und "plan"), Fuellwoerter raus, Rest gestemmt.
 */
export const titleStems = (title: string): string[] =>
    titleStemForms(title).map((f) => f.stem);

/** Gemeinsame Endung zweier Staemme — die Bruecke ueber Komposita. */
const sharedSuffixLength = (a: string, b: string): number => {
    let i = 0;
    while (i < a.length && i < b.length && a[a.length - 1 - i] === b[b.length - 1 - i]) i += 1;
    return i;
};

/**
 * Ab wann eine gemeinsame Endung ein gemeinsames Wort ist.
 *
 * 6 Zeichen: "begehung" verbindet "baustellenbegehung" mit
 * "sicherheitsbegehung", "ung" oder "eit" verbinden nichts.
 */
const MIN_SHARED_SUFFIX = 6;

/**
 * Wie belastbar der Bezug zweier Woerter ist.
 *
 * none   — kein Bezug.
 * weak   — das eine steckt im anderen. Reicht fuer einen VORSCHLAG, aber nicht
 *          fuer die Vorauswahl: "sicherheit" steckt sowohl in
 *          "Sicherheitsbegehung" als auch in "Sicherheits- und
 *          Gesundheitsschutzplan" — zwei voellig verschiedene Leistungen.
 * strong — dasselbe Wort oder dieselbe tragende Endung ("…begehung").
 */
type OverlapKind = 'none' | 'weak' | 'strong';

const wordOverlapKind = (a: string, b: string): OverlapKind => {
    if (a === b || sharedSuffixLength(a, b) >= MIN_SHARED_SUFFIX) return 'strong';
    if (a.length >= MIN_STEM_LENGTH && b.includes(a)) return 'weak';
    if (b.length >= MIN_STEM_LENGTH && a.includes(b)) return 'weak';
    return 'none';
};

/** Beste Bezugsstufe ueber gestemmte UND ungestemmte Form. */
const stemFormOverlapKind = (a: StemForm, b: StemForm): OverlapKind => {
    let best: OverlapKind = 'none';
    for (const left of a.word === a.stem ? [a.stem] : [a.stem, a.word]) {
        for (const right of b.word === b.stem ? [b.stem] : [b.stem, b.word]) {
            const kind = wordOverlapKind(left, right);
            if (kind === 'strong') return 'strong';
            if (kind === 'weak') best = 'weak';
        }
    }
    return best;
};

/** Ein Vorschlag: bestehende Position samt der Signale, die fuer sie sprechen. */
export interface PositionCandidate {
    existing: ExistingPositionRef;
    /** Rangwert; nur zum Sortieren gedacht, nicht zum Anzeigen. */
    score: number;
    /** Einzelpreis auf den Cent gleich — das staerkste Signal. */
    exactUnitPrice: boolean;
    sameUnit: boolean;
    /** Wie viele Wortstaemme des Belegtitels im LV-Titel wiederkehren. */
    tokenOverlap: number;
    /** Davon mit belastbarem Bezug (gleiches Wort oder tragende Endung). */
    strongOverlap: number;
    /**
     * Preis exakt UND belastbarer Titelbezug: nur ein solcher Kandidat wird in
     * der Oberflaeche vorausgewaehlt und vorangehakt.
     *
     * Ein einzelner Teilwort-Treffer genuegt dafuer NICHT: "Honorar
     * Sicherheits- und Gesundheitsschutzplan" traf ueber "sicherheit" die
     * Position "Sicherheitsbegehung" — bei gleichem Einzelpreis waere die
     * falsche Zuordnung vorangehakt durchgelaufen. Als Vorschlag steht sie
     * weiterhin in der Liste.
     */
    strong: boolean;
}

const existingRefOf = (s: ServiceItem): ExistingPositionRef => ({
    id: s.id,
    lvPosition: s.lvPosition || '',
    title: s.title || '',
    unit: s.unit || '',
    unitPriceCents: centsFromEuro(s.unitPrice || 0),
});

const SCORE_EXACT_PRICE = 100;
const SCORE_SAME_UNIT = 10;
const SCORE_PER_STEM = 20;
/** Mehr Vorschlaege als das liest niemand durch — sie waeren wieder Rauschen. */
const MAX_CANDIDATES = 5;

/**
 * Aehnliche LV-Positionen zu einer Belegzeile, beste zuerst.
 *
 * Eine inhaltliche Ueberschneidung im Titel ist PFLICHT, Preis und Einheit
 * gewichten nur. Ohne diese Schranke schluege jede beliebige Position mit
 * zufaellig gleichem Einzelpreis vor — ein Vorschlag ohne Bezug ist schlechter
 * als keiner, weil er zum Wegklicken erzieht.
 */
export const positionCandidates = (
    extracted: ExtractedPosition,
    services: ServiceItem[],
): PositionCandidate[] => {
    const forms = titleStemForms(extracted.title);
    if (!forms.length) return [];
    const unit = normTitle(extracted.unit || '');

    const out: PositionCandidate[] = [];
    for (const service of services) {
        const serviceForms = titleStemForms(service.title || '');
        let tokenOverlap = 0;
        let strongOverlap = 0;
        for (const form of forms) {
            let best: OverlapKind = 'none';
            for (const other of serviceForms) {
                const kind = stemFormOverlapKind(form, other);
                if (kind === 'strong') {
                    best = 'strong';
                    break;
                }
                if (kind === 'weak') best = 'weak';
            }
            if (best === 'none') continue;
            tokenOverlap += 1;
            if (best === 'strong') strongOverlap += 1;
        }
        if (tokenOverlap === 0) continue;
        const existing = existingRefOf(service);
        const exactUnitPrice = existing.unitPriceCents === extracted.unitPriceCents;
        const serviceUnit = normTitle(existing.unit);
        const sameUnit = Boolean(unit) && unit === serviceUnit;
        out.push({
            existing,
            score:
                (exactUnitPrice ? SCORE_EXACT_PRICE : 0) +
                (sameUnit ? SCORE_SAME_UNIT : 0) +
                SCORE_PER_STEM * tokenOverlap,
            exactUnitPrice,
            sameUnit,
            tokenOverlap,
            strongOverlap,
            // Ein einzelnes enthaltenes Teilwort traegt die Vorauswahl nicht —
            // entweder ein belastbarer Treffer oder mindestens zwei Woerter.
            strong: exactUnitPrice && (strongOverlap > 0 || tokenOverlap >= 2),
        });
    }
    return out
        // Titel als zweites Kriterium: gleiche Punktzahl darf nicht von der
        // Reihenfolge im LV abhaengen, sonst wandert die Vorauswahl.
        .sort((a, b) => b.score - a.score || a.existing.title.localeCompare(b.existing.title))
        .slice(0, MAX_CANDIDATES);
};

/**
 * Der eine Kandidat, der vorausgewaehlt werden darf.
 *
 * Genau einer mit exaktem Preis UND Titelbezug — bei zweien waere jede Wahl
 * geraten, und geraten wird hier nicht.
 */
export const preselectedCandidate = (
    candidates: PositionCandidate[] | undefined,
): PositionCandidate | null => {
    const strong = (candidates ?? []).filter((c) => c.strong);
    return strong.length === 1 ? strong[0] : null;
};

/**
 * Preisabweichung, ab der eine gefundene Position zur Entscheidung wird.
 *
 * 0,5 % faengt die Rundungs-/OCR-Unschaerfe ab (49,99 statt 50,00), meldet
 * aber jede echte Preisdifferenz — und die ist relevant: der Nachtrag legt
 * den Betrag fest, der spaeter kumulativ abgerechnet wird.
 */
const PRICE_TOLERANCE = 0.005;

/** Mengenrundung: Stunden kommen als 42,5 und 42,50 aus derselben Quelle. */
const QUANTITY_TOLERANCE = 0.0005;

/**
 * Extrahierte Positionen gegen den Ist-Stand des Projekt-LV.
 *
 * Match-Reihenfolge LV-Nummer → Kunden-LV-Nummer → normalisierter Titel: die
 * Nummer ist die verlaesslichere Identitaet, der Titel nur der Rueckfall fuer
 * Belege ohne Ordnungsnummern. Das Matching IST die Idempotenz-Schranke —
 * derselbe Beleg ein zweites Mal eingelesen findet die beim ersten Mal
 * angelegten Positionen wieder und meldet sie als 'matched'.
 *
 * Erst WENN dieser exakte Weg nichts findet, wird nach aehnlichen Positionen
 * gesucht ('suggested'). Der schnelle Pfad bleibt davon unberuehrt: was exakt
 * trifft, trifft weiterhin exakt.
 */
export const reconcilePositions = (
    extracted: ExtractedPosition[],
    services: ServiceItem[],
): ReconciledPosition[] => {
    const byLv = new Map<string, ServiceItem>();
    const byClientLv = new Map<string, ServiceItem>();
    const byTitle = new Map<string, ServiceItem>();
    for (const s of services) {
        const lv = normTitle(s.lvPosition || '');
        if (lv && !byLv.has(lv)) byLv.set(lv, s);
        const clientLv = normTitle(s.clientLvPosition || '');
        if (clientLv && !byClientLv.has(clientLv)) byClientLv.set(clientLv, s);
        const title = normTitle(s.title || '');
        if (title && !byTitle.has(title)) byTitle.set(title, s);
    }

    return extracted.map((x) => {
        const lvKey = normTitle(x.lvPosition);
        const found =
            (lvKey ? byLv.get(lvKey) ?? byClientLv.get(lvKey) : undefined) ??
            byTitle.get(normTitle(x.title));
        if (!found) {
            const candidates = positionCandidates(x, services);
            return candidates.length
                ? { extracted: x, status: 'suggested' as const, candidates }
                : { extracted: x, status: 'new' as const };
        }

        const existing = existingRefOf(found);
        const diff = Math.abs(existing.unitPriceCents - x.unitPriceCents);
        if (diff > existing.unitPriceCents * PRICE_TOLERANCE) {
            return { extracted: x, status: 'price_mismatch' as const, existing };
        }
        // 'matched' heisst NICHT "Position existiert", sondern "die Menge
        // dieses Belegs steckt bereits im Stand". Nur die abgerechnete Menge
        // beweist das: sie ist es, die der Import setzt.
        //
        // Vorher genuegte der Treffer selbst — und weil eine gefundene Position
        // weder ankreuzbar noch zuordenbar ist, fiel ihre Belegmenge lautlos
        // aus der Uebernahme. Projekt 5940: die Zeile "Sicherheitsbegehungen
        // 42,5 Stunden * 70,00" traf die gleichnamige Position (deren Titel aus
        // genau diesem Beleg stammte), Preis gleich — also 'matched'. Ergebnis:
        // quantity_billed blieb 0, die 42,5 Stunden standen nirgends, und die
        // naechste kumulative Rechnung forderte 2.975,00 EUR zu wenig, weil der
        // Abschlag der Alt-Rechnung abgezogen wurde, ohne dass ihr Leistung
        // gegenuebersteht.
        //
        // Die Idempotenz bleibt: nach der Uebernahme deckt quantityBilled die
        // Belegmenge, derselbe Beleg meldet die Zeile beim zweiten Einlesen
        // wieder als 'matched'.
        const billed = found.quantityBilled || 0;
        if (billed + QUANTITY_TOLERANCE >= x.quantity) {
            return { extracted: x, status: 'matched' as const, existing };
        }
        return {
            extracted: x,
            status: 'suggested' as const,
            // Genau EIN starker Kandidat: die gefundene Position selbst. Damit
            // waehlt initialBackfillTarget sie als Ziel vor und hakt die Zeile
            // an — die Uebernahme hebt dort die abgerechnete Menge auf den
            // Beleg-Stand, statt eine zweite Position anzulegen.
            candidates: [
                {
                    existing,
                    score: SCORE_EXACT_PRICE + SCORE_SAME_UNIT + SCORE_PER_STEM,
                    exactUnitPrice: true,
                    sameUnit: normTitle(existing.unit) === normTitle(x.unit || ''),
                    tokenOverlap: 1,
                    strongOverlap: 1,
                    strong: true,
                },
            ],
        };
    });
};

/** Befund der Gegenrechnung Positionssumme gegen Belegsumme. */
export interface SumDeviation {
    sumCents: number;
    referenceCents: number;
    /** Betrag der Abweichung, VORZEICHENBEHAFTET: Positionen minus Beleg. */
    differenceCents: number;
    /** Betrag der Abweichung, bezogen auf die Belegsumme. Immer positiv. */
    ratio: number;
}

/**
 * Plausibilitaets-Gegenrechnung: Summe der Positionen gegen die Belegsumme.
 *
 * Kein Blocker, sondern ein Hinweis: weicht die Summe der erkannten Zeilen von
 * der ausgewiesenen Rechnungssumme ab, wurde entweder eine Position uebersehen
 * oder eine Summenzeile mitgelesen — beides sieht in der Tabelle harmlos aus.
 * `null`, wenn der Beleg keine Vergleichssumme hergibt.
 *
 * Die RICHTUNG steht seit jeher in den Zahlen, wurde aber nicht ausgewertet:
 * `differenceCents` macht sie lesbar. Sie entscheidet, welcher Fall vorliegt —
 * siehe discountFromDeviation.
 */
export const positionsSumDeviation = (
    positions: ExtractedPosition[],
    referenceNetCents: number | null | undefined,
): SumDeviation | null => {
    if (!referenceNetCents || referenceNetCents <= 0) return null;
    const sum = positions.reduce((acc, p) => acc + p.totalCents, 0);
    return {
        sumCents: sum,
        referenceCents: referenceNetCents,
        differenceCents: sum - referenceNetCents,
        ratio: Math.abs(sum - referenceNetCents) / referenceNetCents,
    };
};

/** Ein Nachlass, der die Abweichung erklaeren wuerde. */
export interface DeviationDiscount {
    /** Netto-Differenz zwischen Positionssumme und Belegsumme. */
    amountCents: number;
    /** Derselbe Betrag als Satz auf die Positionssumme, auf zwei Stellen. */
    percent: number;
}

/**
 * Laesst sich die Abweichung durch einen Nachlass erklaeren?
 *
 * NUR wenn die Positionen MEHR ergeben als der Beleg fordert. Die Richtung ist
 * der ganze Punkt:
 *
 *   Positionen > Beleg   Der Beleg forderte weniger, als seine Zeilen ergeben.
 *                        Genau das tut ein Nachlass — und genau so sahen die
 *                        alten Teilleistungsrechnungen aus.
 *
 *   Positionen < Beleg   Der Beleg forderte MEHR, als die erkannten Zeilen
 *                        hergeben. Ein Nachlass kann das nicht erklaeren; dort
 *                        fehlt eine Zeile, und die fehlt hinterher auch im
 *                        Leistungsstand des Projekts.
 *
 * Deshalb gibt diese Funktion im zweiten Fall `null` zurueck und die Sperre
 * bleibt, wo sie hingehoert.
 *
 * Der Satz ist auf die POSITIONSSUMME bezogen, nicht auf die Belegsumme: so
 * wird ein Nachlass kaufmaennisch gerechnet ("3 % Nachlass auf die
 * Auftragssumme"), und nur so ist die Zahl mit dem Beleg vergleichbar.
 */
export const discountFromDeviation = (
    deviation: SumDeviation | null | undefined,
): DeviationDiscount | null => {
    if (!deviation) return null;
    if (deviation.differenceCents <= 0) return null;
    if (deviation.sumCents <= 0) return null;
    return {
        amountCents: deviation.differenceCents,
        percent: Math.round((deviation.differenceCents / deviation.sumCents) * 10000) / 100,
    };
};

/** Herkunfts-Marker am nachgetragenen LV-Eintrag (ServiceItem.category). */
export const BACKFILL_CATEGORY = 'Altbestand';

/**
 * Praefix der Buchungsreferenz nachgetragener Leistungen.
 *
 * Bewusst NICHT "BU-…": ein solcher Eintrag gilt projektweit als
 * Abrechnungsvorgang (serviceLogUtils.isAccountingEntry) und wuerde aus der
 * Zeitraumauswertung fallen. Der Nachtrag ist eine erbrachte Leistung, kein
 * Buchungssatz — er soll gezaehlt werden.
 */
export const BACKFILL_REFERENCE_PREFIX = 'ALT-';

/**
 * Buchungsreferenz eines eingelesenen Altbelegs.
 *
 * `customerId` haengt eine Kennung an ('ALT-26021019#3f2a1c9b'). Gebraucht wird
 * sie bei geteilten Projekten: dort kann dieselbe Nummer aus dem Altsystem an
 * MEHRERE Auftraggeber gegangen sein (Belegkreis je Projekt statt je Kunde).
 * Ohne Unterscheidung truege der zweite Beleg dieselbe Referenz wie der erste
 * — und revertBackfill naehme beim Einlesen des zweiten die Menge des ersten
 * zurueck.
 *
 * Der Zusatz ist bewusst NICHT Teil der Belegnummer: backfillInvoiceNumberOf
 * schneidet ihn wieder ab, damit Anzeige und Deckungsnachweis weiterhin die
 * Nummer nennen, die auf dem Papier steht.
 */
export const backfillReferenceFor = (invoiceNumber: string, customerId?: string): string =>
    `${BACKFILL_REFERENCE_PREFIX}${invoiceNumber.trim()}${
        customerId ? `#${customerId.replace(/-/g, '').slice(0, 8)}` : ''
    }`;

export interface BackfillSelection {
    position: ExtractedPosition;
    /**
     * Der zu uebernehmende Einzelpreis in Cent. Vorbelegt mit dem Preis der
     * Alt-Rechnung; bei 'price_mismatch' darf der Nutzer je Zeile auf den
     * LV-Preis wechseln.
     */
    unitPriceCents: number;
    /**
     * Bestehende Position, auf die die Menge gebucht wird.
     *
     * Gesetzt heisst: KEINE neue Position. Die Alt-Menge erhoeht quantityUsed
     * der bestehenden Position, die Protokollzeile haengt an deren id. Preis
     * und Abrechnungsmenge der bestehenden Position bleiben unberuehrt — der
     * Nachtrag traegt Leistung nach, er korrigiert kein LV.
     *
     * Fehlt das Feld, entsteht wie bisher eine Position der Kategorie
     * 'Altbestand'.
     */
    targetServiceId?: string;
}

export interface BackfillOptions {
    /** Leistungsdatum des Protokolleintrags (ISO YYYY-MM-DD). */
    serviceDate: string;
    /** Nummer der Alt-Rechnung — traegt die Herkunft ins Protokoll. */
    invoiceNumber: string;
    performerId: string;
    performerName: string;
    /** ID-Fabrik (crypto.randomUUID im Aufrufer) — haelt den Kern rein. */
    newId: () => string;
    /**
     * Auftraggeber, dem dieser Altbeleg gehoert.
     *
     * Bei einem geteilten Projekt ist er PFLICHT — dort deckt der Beleg nur
     * den Anteil EINES Auftraggebers ab, und ohne Stempel liesse sich der
     * Stand je Auftraggeber nicht mehr bilden. Ohne Aufteilung darf er fehlen;
     * die Zeile ist dann Altbestand ohne Zuordnung.
     */
    customerId?: string;
    /**
     * Der TEILNEHMER, dem dieser Altbeleg gehoert (project_splits.id).
     *
     * Steht neben `customerId` und nicht an ihrer Stelle: der Auftraggeber
     * bleibt die Angabe, die jede Auswertung ausserhalb der Aufteilung liest.
     * Zuordenbar macht die Zeile aber erst der Teilnehmer, sobald ein
     * Auftraggeber mehrere Abschnitte hat.
     */
    partyId?: string;
}

/**
 * Was das Einlesen bei einem GETEILTEN Projekt braucht.
 *
 * Der Grund, warum es diesen Zusatz gibt: applyBackfillIncrements hebt den
 * Zaehler auf den Stand des Belegs (max), es addiert bewusst nicht. Bei EINEM
 * Auftraggeber ist das richtig — die Alt-Rechnung ist eine Aussage ueber den
 * Stand. Bei DREI Auftraggebern ist es falsch: drei Belege ueber je ein
 * Drittel ergaeben ueber max ein Drittel, und zwei Drittel der Menge saehen
 * unabgerechnet aus und wuerden erneut gefordert. Addieren waere ebenso
 * falsch, dann verdoppelte derselbe Beleg beim zweiten Einlesen.
 *
 * Aufloesung: die Stand-Regel gilt JE AUFTRAGGEBER, und der Projektzaehler
 * bewegt sich um die Differenz, die dieser Auftraggeber dabei macht.
 */
export interface BackfillCustomerContext {
    /**
     * Der TEILNEHMER, dessen Alt-Rechnung eingelesen wird (project_splits.id).
     *
     * Feiner als `customerId` und seit den Abschnitten (SQL Nr. 54) die
     * massgebliche Groesse: bei vier Haeusern desselben Auftraggebers ist sein
     * Stand die Summe aller vier, und der Zuwachs dieses Belegs waere daran
     * nicht mehr abzulesen.
     */
    partyId: string;
    customerId: string;
    /**
     * Aufloesung fuer ALT-Zeilen aus der Zeit vor den Abschnitten, die nur den
     * Auftraggeber tragen. Siehe projectSplit.legacyPartyByCustomer.
     */
    legacy?: ReadonlyMap<string, string>;
    /**
     * Leistungsprotokoll NACH revertBackfill — der Stand, gegen den gerechnet
     * wird. Mit dem Protokoll VOR der Ruecknahme gerechnet zaehlte der eigene
     * erste Lauf als fremder Stand, und das zweite Einlesen desselben Belegs
     * bewegte nichts mehr.
     */
    serviceLog: ServiceTransaction[];
}

/**
 * Ausgewaehlte Alt-Positionen in LV-Eintraege + datierte Protokollzeilen.
 *
 * beauftragt = erledigt (status 'done', "1 von 1"): die Leistung IST erbracht,
 * sie wurde ja bereits berechnet. Ein 'open'-Eintrag wuerde das Projekt
 * dauerhaft als unfertig zeigen und die naechste Rechnung wuerde die Menge
 * erneut anbieten.
 *
 * quantityBilled = die nachgetragene Menge: was auf einer Abschlagsrechnung
 * steht, IST abgerechnet. Der Beleg dazu ist die Alt-Rechnung selbst, deren
 * Nummer in der Buchungsreferenz steht — keine Buchung ohne Beleg. Damit
 * verschwindet die Menge aus jeder Folgerechnung und aus jeder Ansicht
 * offener Leistung, statt nur in einer Anzeigeschicht kaschiert zu werden.
 *
 * In BEIDEN Abrechnungsmodellen richtig, und das ist der Grund, warum der
 * Wert hier stehen darf:
 *   fortschreibend — computeOpenPositions rechnet used - billed, die
 *                    Alt-Menge faellt heraus. Ein Abzug existiert in diesem
 *                    Modell nicht, es kann also nichts doppelt abgezogen
 *                    werden.
 *   kumulativ      — computeCumulativePositions nimmt den Gesamtstand und
 *                    sieht quantityBilled gar nicht an; der Abzug der
 *                    Vorrechnungen sitzt im Summenblock. Die Zeile gewinnt
 *                    lediglich ein korrektes previouslyBilledQty.
 *
 * Ohne die Protokollzeile kennt nur das LV die Menge — als Gesamtsumme ohne
 * Datum. Eine Zeitraumabrechnung findet sie dann nicht (siehe
 * ProjectSupplementModal: derselbe Grund, dasselbe Muster).
 *
 * ZWEI Wege, ein Ergebnis: mit targetServiceId entsteht KEINE Position, nur
 * die Protokollzeile an der bestehenden id — die Mengenerhoehung liefert
 * backfillQuantityIncrements/applyBackfillIncrements, damit diese Funktion
 * ohne den Ist-Stand des LV auskommt.
 *
 * Pur: keine Uhr, kein crypto — Zeit und IDs kommen ueber opts.
 */
export const buildBackfillServices = (
    selected: BackfillSelection[],
    opts: BackfillOptions,
): { services: ServiceItem[]; log: ServiceTransaction[] } => {
    // Ohne Datum darf hier NICHTS entstehen. Eine nachgetragene Menge ohne
    // Protokolldatum gehoert zu keinem Monat, faellt durch jede
    // Zeitraumauswertung und kippt kumulativ jeden spaeteren Beleg in die
    // Sperre: die Abschlaege werden voll abgezogen, die Grundlage schrumpft
    // auf das Datierte (Projekt 5634 — drei Pauschalpositionen ueber 1.830,00
    // ohne einen einzigen Eintrag, Monate spaeter liess sich keine Rechnung
    // mehr stellen).
    //
    // Die Oberflaeche sperrt die Uebernahme bereits (missingServiceDate in
    // ExternalAdvancesPanel). Der Riegel steht trotzdem hier: er gilt fuer
    // JEDEN kuenftigen Aufrufer, auch fuer einen Stapelimport ohne Maske. Und
    // er wirft, statt ein Ersatzdatum zu erfinden — welcher Tag gemeint ist,
    // weiss nur der, der die Alt-Rechnung vor sich hat.
    if (selected.length > 0 && !/^\d{4}-\d{2}-\d{2}$/.test((opts.serviceDate ?? '').trim())) {
        throw new Error(
            'Für den Nachtrag aus einer Alt-Rechnung fehlt das Leistungsdatum (JJJJ-MM-TT). Ohne Datum gehört die Menge zu keinem Monat und fehlt in jeder Zeitraumabrechnung.',
        );
    }
    const services: ServiceItem[] = [];
    const log: ServiceTransaction[] = [];
    const reference = backfillReferenceFor(opts.invoiceNumber, opts.customerId);

    for (const sel of selected) {
        const p = sel.position;
        const target = sel.targetServiceId?.trim();
        let id: string;
        if (target) {
            id = target;
        } else {
            id = opts.newId();
            // Eine Teilleistung ist NICHT erledigt: beauftragt bleibt die volle
            // Menge, erbracht und abgerechnet ist nur der Anteil, und der
            // Status sagt das auch. Sonst zeigte das Projekt die Position als
            // fertig, und der offene Rest liesse sich nie mehr abrechnen.
            const contracted = p.contractedQuantity ?? p.quantity;
            services.push({
                id,
                lvPosition: p.lvPosition,
                title: p.title,
                unit: p.unit,
                quantityContracted: contracted,
                quantityUsed: p.quantity,
                quantityBilled: p.quantity,
                unitPrice: euroFromCents(sel.unitPriceCents),
                status: p.quantity < contracted ? 'open' : 'done',
                category: BACKFILL_CATEGORY,
            });
        }
        log.push({
            id: opts.newId(),
            date: opts.serviceDate,
            serviceId: id,
            quantity: p.quantity,
            performerId: opts.performerId,
            performerName: opts.performerName,
            isSubstitution: false,
            // Nicht "Nachtrag": das liest sich wie ein Nachtrag im Sinne der
            // VOB, also wie zusaetzlich beauftragte Leistung. Gemeint ist das
            // Gegenteil — bereits berechnete Vergangenheit.
            description: `Bereits abgerechnet mit Rechnung ${opts.invoiceNumber.trim()} (${p.quantity} ${p.unit}${
                p.sharePercent != null ? `, ${p.sharePercent} % Teilleistung` : ''
            })`.trim(),
            bookingReference: reference,
            // Ohne diesen Stempel ist die Menge Altbestand ohne Zuordnung und
            // sperrt jede spaetere Aufteilung des Projekts
            // (projectSplit.unassignedBilledIssues).
            customerId: opts.customerId,
            splitPartyId: opts.partyId,
        });
    }

    return { services, log };
};

/**
 * Zuordnungswerte der Import-Oberflaeche.
 *
 * Hier und nicht in der Komponente, damit die Regel darunter geprueft werden
 * kann — die Oberflaeche selbst ist ohne DOM-Testumgebung nicht erreichbar.
 */
export const BACKFILL_TARGET_NEW = '__new__';
export const BACKFILL_TARGET_UNSET = '';

/**
 * Vorbelegte Zuordnung einer Abgleichszeile.
 *
 * Die Leitplanke: was im Projekt FEHLT, wird angelegt — auch dann, wenn sich
 * kein eindeutiges Ziel finden laesst. Eine Belegzeile steht auf einer bereits
 * gestellten Rechnung, sie ist erbracht und bezahlt verlangt worden. Kommt sie
 * hier nicht ins Projekt, zieht die naechste kumulative Rechnung einen
 * Abschlag ab, dem keine Leistung gegenuebersteht, und blockiert an der
 * fehlenden Deckung — sichtbar wird das erst Monate spaeter und ohne Hinweis,
 * WELCHE Position fehlt (Projekt 6193: im LV standen nur die Begehungen, jede
 * uebrige Belegzeile fand nur eine schwache Aehnlichkeit und blieb dadurch
 * unentschieden und nicht ankreuzbar).
 *
 * Zugeordnet wird nur bei genau EINEM starken Kandidaten (Einzelpreis exakt
 * gleich UND belastbarer Titelbezug) — dann waechst die Menge an der
 * bestehenden Position und es entsteht keine zweite. Alles andere legt an:
 * eine Dublette steht sichtbar im LV und ist korrigierbar, eine fehlende
 * Leistung nicht.
 *
 * 'matched'        — die gefundene Position, nichts wird angelegt.
 * 'price_mismatch' — neu anlegen; die Zeile ist bewusst nicht vorangehakt,
 *                    denn die Position steht bereits im LV.
 */
export const initialBackfillTarget = (r: ReconciledPosition): string => {
    if (r.status === 'matched') return r.existing?.id ?? BACKFILL_TARGET_NEW;
    if (r.status === 'suggested') {
        const strong = preselectedCandidate(r.candidates);
        return strong ? strong.existing.id : BACKFILL_TARGET_NEW;
    }
    return BACKFILL_TARGET_NEW;
};

/**
 * Startet die Zeile angehakt?
 *
 * Alles, was im Projekt fehlt ('new' und 'suggested'). 'matched' ist gar nicht
 * ankreuzbar. 'price_mismatch' bleibt opt-in: die Position steht bereits im
 * LV, nur der Preis weicht ab — vorangehakt waere das ein Klick-durch zur
 * doppelten Leistung.
 */
export const isBackfillPreselected = (status: PositionMatchStatus): boolean =>
    status === 'new' || status === 'suggested';

/** Eine Abgleichszeile, soweit die Uebernahme-Regel sie betrifft. */
export interface BackfillTargetChoice {
    status: PositionMatchStatus;
    target: string;
}

/**
 * Liegt noch eine unentschiedene Zuordnung vor?
 *
 * Solange ja, darf NICHT uebernommen werden. Eine offene Zeile stillschweigend
 * zu ueberspringen waere die teuerste Variante: die Uebernahme verbraucht die
 * Referenz ALT-<Nummer>, der zweite Import desselben Belegs meldet daraufhin
 * "bereits uebernommen" — und die uebersprungene Menge fehlte dauerhaft, ohne
 * dass es irgendwo auffiele. Entschieden ist eine Zeile auch dann, wenn der
 * Nutzer sie nach der Zuordnung wieder abwaehlt; das ist eine Entscheidung.
 */
export const hasUnassignedBackfillRows = (rows: BackfillTargetChoice[]): boolean =>
    rows.some((r) => r.status === 'suggested' && r.target === BACKFILL_TARGET_UNSET);

/**
 * Liegen Belegzeile und Zielposition in verschiedenen Einheitenklassen?
 *
 * Eine Menge traegt ihre Bedeutung nicht mit sich: applyBackfillIncrements
 * addiert die rohe Belegmenge auf quantityUsed UND quantityBilled und behaelt
 * dabei bewusst Einheit und Einzelpreis der Zielposition. Wandern so "89 Std"
 * auf eine Stueck-Position, werden Stunden mit einem Stueckpreis multipliziert
 * und der Betrag steht dauerhaft in der Datenbank. Die Kandidatensuche schuetzt
 * davor nicht: gleiche Einheit gewichtet dort nur (SCORE_SAME_UNIT), und die
 * Vorauswahl verlangt Preis und Titel, nicht die Einheit.
 *
 * Genau EINE Seite in Stunden ist die Abweichung. Fehlt eine Einheit, ist sie
 * unbekannt und nicht falsch — auf fehlende Daten wird nicht gesperrt.
 *
 * Bewusst KEINE Umrechnung: es gibt keinen gespeicherten Faktor zwischen
 * Stunden und Stueck, und geraten waere schlimmer als gesperrt.
 */
export const unitMismatch = (documentUnit?: string | null, serviceUnit?: string | null): boolean => {
    const doc = (documentUnit || '').trim();
    const svc = (serviceUnit || '').trim();
    if (!doc || !svc) return false;
    return isHourlyUnit(doc) !== isHourlyUnit(svc);
};

/**
 * Zugeordnete Zielpositionen, die es im Projekt nicht (mehr) gibt.
 *
 * Zwischen Abgleich und Uebernahme liegt ein Neuladen des Projekts: wurde die
 * Zielposition inzwischen geloescht oder umbenannt-neu-angelegt, ginge die
 * Protokollzeile auf eine serviceId ins Leere. Die Menge waere gebucht, aber an
 * keiner Position sichtbar — und die ALT-Referenz gaelte als verbraucht, sodass
 * ein zweiter Anlauf gewarnt startet. Deshalb VOR dem Schreiben pruefen und im
 * Zweifel gar nichts schreiben.
 */
export const missingBackfillTargets = (
    selected: BackfillSelection[],
    services: ServiceItem[],
): string[] => {
    const known = new Set(services.map((s) => s.id));
    const missing: string[] = [];
    for (const sel of selected) {
        const target = sel.targetServiceId?.trim();
        if (target && !known.has(target) && !missing.includes(target)) missing.push(target);
    }
    return missing;
};

/**
 * Nachzutragende Menge je bestehender Position (nur Zeilen mit Zuordnung).
 *
 * Traegt die Belegeinheiten der Menge mit — nicht nur zur Zierde: sie sind die
 * einzige Stelle im Datenfluss, an der applyBackfillIncrements spaeter noch
 * pruefen kann, ob Beleg- und Zielposition zur selben Einheitenklasse gehoeren
 * (siehe unitMismatch).
 *
 * ALLE beitragenden Einheiten, nicht nur die der ersten Zeile: die Zielauswahl
 * im ExternalAdvancesPanel bietet jede Leistung an und entdoppelt nicht ueber
 * Zeilen hinweg, dieselbe Zielposition kann also mehrfach gewaehlt werden.
 * Behielte die Summe nur die erste Einheit, verschwaende eine spaetere
 * Stundenzeile hinter einer Stueckzeile — 1 Stk + 89 Std ergaeben "90 Stk", die
 * Pruefung liefe gegen die falsche Einheit und der Riegel fiele genau in dem
 * Fall aus, fuer den es ihn gibt.
 */
export const backfillQuantityIncrements = (
    selected: BackfillSelection[],
): Map<string, { quantity: number; documentUnits: string[] }> => {
    const out = new Map<string, { quantity: number; documentUnits: string[] }>();
    for (const sel of selected) {
        const target = sel.targetServiceId?.trim();
        if (!target) continue;
        const prev = out.get(target);
        const documentUnits = prev ? [...prev.documentUnits] : [];
        const unit = (sel.position.unit || '').trim();
        if (unit && !documentUnits.includes(unit)) documentUnits.push(unit);
        out.set(target, {
            quantity: (prev?.quantity ?? 0) + (sel.position.quantity || 0),
            documentUnits,
        });
    }
    return out;
};

/**
 * Bringt die abgerechnete Menge zugeordneter Positionen auf den Stand, den die
 * Alt-Rechnung ausweist — und hebt die erledigte Menge nur so weit an, dass sie
 * nicht darunter faellt.
 *
 * NICHT ADDIEREN. Eine Alt-Rechnung ist eine Aussage ueber den STAND ("von
 * dieser Position sind N Einheiten abgerechnet"), nicht ueber einen Zuwachs.
 * Frueher stand hier `+ inc.quantity` auf beiden Zaehlern, und in Projekt
 * 26021019 wurde daraus: 10 protokollierte Begehungen plus 4 aus der
 * Alt-Rechnung = 14 erbrachte bei 10 beauftragten. Dieselbe Rechnung war
 * ausserdem schon manuell verbucht, weshalb auch die abgerechnete Menge auf 8
 * statt 4 stand.
 *
 * Die Fehlerrichtung ist der Grund fuer `max`: zu viel abgerechnet ist eine
 * Doppelforderung an den Kunden, zu wenig faellt bei der naechsten Rechnung
 * auf. Von zwei unsicheren Wegen ist der zweite der guenstigere.
 *
 * quantityUsed zieht mit, damit keine Position mehr abgerechnet als erbracht
 * ausweist — ein Zustand, den jede Bestandspruefung zu Recht anmahnt. War die
 * Leistung dagegen laengst protokolliert (der Normalfall im laufenden
 * Projekt), bleibt sie unveraendert stehen.
 *
 * Mit `zeit` (Protokoll + Leistungsdatum des Belegs) zieht quantityUsed
 * zusaetzlich die Leistung NACH dem Leistungsdatum mit — siehe nextUsed. Ohne
 * `zeit` gilt die reine Anhebe-Regel wie bisher.
 *
 * unitPrice, quantityContracted und status bleiben, wie sie sind: die
 * Alt-Rechnung verhandelt weder den Vertrag noch den Preis nach. Eine ueber
 * den Vertrag hinausgehende Menge blockiert nichts (Vertragsueberschreitung
 * ist im Rechnungsmodul ein Hinweis, kein Riegel).
 *
 * ZWEITE Verteidigungslinie gegen die Einheiten-Falle: ExternalAdvancesPanel
 * sperrt den "Uebernehmen"-Button bereits bei Einheiten-Klassen-Konflikt,
 * aber das ist eine UI-Regel und schuetzt nur diesen einen Aufrufer. Dieser
 * Kern bleibt ohne eigene Sperre auch fuer Tests und kuenftige Aufrufer
 * erreichbar — deshalb wird hier zusaetzlich per unitMismatch geprueft und
 * bei Konflikt geworfen (house pattern, siehe invoiceWriteBackLogic
 * bookServiceUpdates: "Ueberbuchung"/"Storno"-Fehler statt stillem Verwerfen).
 * Eine unbekannte Einheit (leer auf einer Seite) blockiert nichts.
 *
 * Geprueft wird JEDE beitragende Belegeinheit: sind mehrere Zeilen auf dieselbe
 * Position gelaufen, reicht eine einzige abweichende, um die Summe unbrauchbar
 * zu machen.
 */
export const applyBackfillIncrements = (
    services: ServiceItem[],
    increments: Map<string, { quantity: number; documentUnits: string[] }>,
    context?: BackfillCustomerContext,
    zeit?: BackfillServiceDate,
): ServiceItem[] => {
    if (increments.size === 0) return services;
    const altStands = context
        ? altCoveredByParty(context.serviceLog, context.legacy ?? new Map())
        : null;
    return services.map((s) => {
        const inc = increments.get(s.id);
        if (!inc || !inc.quantity) return s;
        const conflicting = inc.documentUnits.find((u) => unitMismatch(u, s.unit));
        if (conflicting) {
            throw new Error(
                `Einheiten passen nicht zusammen bei "${s.title}": Beleg in ${conflicting}, Position in ${s.unit}.`,
            );
        }
        const quantityBilled = nextBilled(s, inc.quantity, context, altStands);
        return {
            ...s,
            quantityUsed: nextUsed(s, quantityBilled, inc.quantity, context, zeit),
            quantityBilled,
        };
    });
};

/**
 * Zeitbezug des Altbelegs: das Protokoll, gegen das gerechnet wird (NACH
 * revertBackfill), und das Leistungsdatum aus dem Einlesedialog.
 */
export interface BackfillServiceDate {
    serviceLog: ServiceTransaction[];
    /** Leistungsdatum des Belegs (YYYY-MM-DD); Vorbelegung ist das Rechnungsdatum. */
    serviceDate: string;
}

/**
 * Erbrachte Leistung einer Position NACH dem Leistungsdatum eines Altbelegs.
 *
 * Sie kann nicht in diesem Beleg stecken. Gezaehlt wird nur Leistung: keine
 * Buchung (isPostingEntry — BU/VZ/RN/MB, manuelle Verbuchung) und keine
 * Bestandsuebernahme (ALT). Derselbe Tag zaehlt NICHT als danach — eine
 * Begehung am Rechnungstag kann auf dem Beleg stehen, und im Zweifel wird
 * lieber zu wenig erbracht angenommen als eine Doppelforderung erzeugt.
 */
export const performedAfterServiceDate = (
    serviceLog: ServiceTransaction[],
    serviceId: string,
    serviceDate: string,
): number => {
    const stichtag = buchungstag(serviceDate);
    if (!stichtag) return 0;
    let sum = 0;
    for (const t of serviceLog) {
        if (!t || t.serviceId !== serviceId) continue;
        if (isPostingEntry(t) || isBackfilledAltEntry(t)) continue;
        const tag = buchungstag(t.date);
        if (!tag || tag <= stichtag) continue;
        sum += Number(t.quantity) || 0;
    }
    return Math.max(0, Math.round(sum * 1000) / 1000);
};

/**
 * Die erbrachte Menge EINER Position nach dem Einlesen.
 *
 * Grundregel: mindestens so viel erbracht wie abgerechnet (max). Sie sieht
 * aber nicht, ob Protokoll und Beleg dieselbe Leistung meinen oder
 * verschiedene. Projekt 5284 (29.09.2026): abgerechnet laut Alt-Rechnungen
 * 102 Begehungen bis Juni, im Protokoll 91 bis Juni plus 7 danach. Die
 * Anhebe-Regel ergab 102 erbracht — die 7 Begehungen nach dem Beleg waren
 * damit als abgerechnet verbucht, und die naechste kumulative Rechnung haette
 * sie nicht gefordert. Ebenso 5940 (Berichte #18–#35 alle nach dem Beleg,
 * damals per Datenfix nachgetragen).
 *
 * Was NACH dem Leistungsdatum protokolliert ist, steckt nicht im Beleg und
 * kommt deshalb auf den neuen Abrechnungsstand oben drauf:
 *   erbracht = max(bisher, abgerechnet neu + Leistung nach dem Leistungsdatum)
 *
 * Nur wenn DIESER Beleg den Stand setzt (Belegmenge ueber dem bisherigen
 * Abrechnungsstand). Liegt der Stand schon hoeher, stammt er von einem
 * spaeteren Beleg, und dessen Zeitraum deckt einen Teil der Leistung „nach"
 * diesem Datum — sie hier zu addieren zaehlte sie doppelt.
 *
 * Bei geteilten Projekten (context) unveraendert die Grundregel: dort deckt
 * jeder Beleg nur den Anteil seines Auftraggebers, die Leistung danach kann
 * vom Beleg eines anderen Beteiligten mit spaeterem Datum gedeckt sein.
 */
const nextUsed = (
    service: ServiceItem,
    billedAfter: number,
    documentQuantity: number,
    context: BackfillCustomerContext | undefined,
    zeit: BackfillServiceDate | undefined,
): number => {
    const usedBefore = service.quantityUsed || 0;
    const floor = Math.max(usedBefore, billedAfter);
    if (!zeit || context) return floor;
    if (documentQuantity <= (service.quantityBilled || 0) + QUANTITY_TOLERANCE) return floor;
    const danach = performedAfterServiceDate(zeit.serviceLog, service.id, zeit.serviceDate);
    return Math.max(floor, Math.round((billedAfter + danach) * 1000) / 1000);
};

/**
 * Was das Einlesen je Position TATSAECHLICH veraendert — die Differenz, die
 * `applyBackfillIncrements` bewirkt.
 *
 * Sie wandert in die ALT-Protokollzeile (appliedUsed/appliedBilled), damit
 * revertBackfill exakt das zuruecknehmen kann, was geschrieben wurde. Seit die
 * Zaehler angehoben statt addiert werden, ist die Belegmenge dafuer nicht mehr
 * das richtige Mass: sie ist oft groesser als die Wirkung.
 */
/**
 * Der neue Abrechnungsstand EINER Position nach dem Einlesen.
 *
 * Eine einzige Stelle fuer beide Aufrufer (Anwenden und Stempeln): liefen sie
 * auseinander, waere die Ruecknahme nicht mehr die Umkehrung des Einlesens —
 * genau der Fehler, gegen den appliedUsed/appliedBilled ueberhaupt eingefuehrt
 * wurden.
 */
const nextBilled = (
    service: ServiceItem,
    documentQuantity: number,
    context: BackfillCustomerContext | undefined,
    altStands: Map<string, { byParty: Map<string, number>; unassigned: number }> | null,
): number => {
    const current = service.quantityBilled || 0;
    if (!context || !altStands) {
        // Ungeteiltes Projekt: die Alt-Rechnung nennt den Stand. Anheben, nie
        // addieren — sonst wurden aus 10 protokollierten Begehungen plus 4 aus
        // der Alt-Rechnung 14 erbrachte bei 10 beauftragten (26021019).
        return Math.max(current, documentQuantity);
    }
    // Geteiltes Projekt: die Stand-Regel gilt je Auftraggeber, und der
    // Projektzaehler bewegt sich um DESSEN Zuwachs. Bei drei Belegen ueber je
    // ein Drittel summiert er sich damit auf den vollen Stand, waehrend
    // derselbe Beleg zweimal eingelesen weiterhin nichts bewegt.
    const standBefore = billedFor(altStands.get(service.id), context.partyId);
    const standAfter = Math.max(standBefore, documentQuantity);
    return current + (standAfter - standBefore);
};

export const backfillEffectiveIncrements = (
    services: ServiceItem[],
    increments: Map<string, { quantity: number; documentUnits: string[] }>,
    context?: BackfillCustomerContext,
    zeit?: BackfillServiceDate,
): Map<string, { used: number; billed: number }> => {
    const out = new Map<string, { used: number; billed: number }>();
    const altStands = context
        ? altCoveredByParty(context.serviceLog, context.legacy ?? new Map())
        : null;
    for (const [serviceId, inc] of increments) {
        const service = services.find((s) => s.id === serviceId);
        if (!service || !inc.quantity) continue;
        const billedBefore = service.quantityBilled || 0;
        const usedBefore = service.quantityUsed || 0;
        const billedAfter = nextBilled(service, inc.quantity, context, altStands);
        const usedAfter = nextUsed(service, billedAfter, inc.quantity, context, zeit);
        out.set(serviceId, {
            used: usedAfter - usedBefore,
            billed: billedAfter - billedBefore,
        });
    }
    return out;
};

/**
 * Schreibt die tatsaechliche Wirkung in die ALT-Zeilen eines Einlesevorgangs.
 *
 * `quantity` bleibt unangetastet: die Zeile sagt weiterhin, welche Menge der
 * Beleg abgegolten hat — daran haengen die Anzeige und altCoverageByService.
 */
export const stampAppliedIncrements = (
    log: ServiceTransaction[],
    effective: Map<string, { used: number; billed: number }>,
): ServiceTransaction[] =>
    log.map((entry) => {
        const applied = effective.get(entry.serviceId);
        if (!applied) return entry;
        return { ...entry, appliedUsed: applied.used, appliedBilled: applied.billed };
    });

// ═══════════════════════════════════════════════════════════════════════════
// Was die Alt-Rechnung bereits abgegolten hat
// ═══════════════════════════════════════════════════════════════════════════

/**
 * DAS PROBLEM: quantityBilled bleibt bei nachgetragenen Mengen 0 — und muss es
 * auch, denn abgerechnet wird der Gesamtstand von der kumulativen Rechnung,
 * deren Abzug die Alt-Abschlaege bereits enthaelt. Eine Abrechnungsmenge ohne
 * Beleg waere eine Buchung ohne Beleg.
 *
 * Fuer die ANZEIGE ist das aber irrefuehrend: die Menge steht als "offen /
 * nicht abgerechnet" da, obwohl sie mit der Alt-Rechnung laengst gefordert
 * wurde. Die Deckung wird deshalb aus den ALT-Protokollzeilen gerechnet — dort
 * und nur dort steht, welche Menge welcher Altbeleg abgegolten hat.
 *
 * Ausschliesslich Darstellung: keine Summe der kumulativen Rechnung und keine
 * Bemessungsgrundlage eines Abzugs wird hieraus gebildet.
 */
export interface AltCoverage {
    /** Von Alt-Rechnungen abgegoltene Menge dieser Leistung. */
    quantity: number;
    /** Die Belegnummern dazu, aufsteigend und ohne Dubletten. */
    invoiceNumbers: string[];
}

/**
 * Traegt der Eintrag die Herkunft "Alt-Rechnung"? (Form, nicht Menge.)
 *
 * Die Herkunftsfrage beantwortet `isBackfilledAltEntry` (serviceLogUtils):
 * die Journal-Buchungsart `legacy_backfill`, sonst das Praefix `ALT-`. Die
 * Zusatzbedingung bleibt hier — ohne `serviceId` deckt die Zeile keine
 * Position, und genau danach wird gefragt.
 */
const isBackfillEntry = (t: ServiceTransaction): boolean =>
    Boolean(t?.serviceId) && isBackfilledAltEntry(t);

/** Belegnummer aus 'ALT-<Nummer>'; leer, wenn die Referenz keine traegt. */
export const backfillInvoiceNumberOf = (bookingReference: string | undefined): string =>
    bookingReference?.startsWith(BACKFILL_REFERENCE_PREFIX)
        ? // Alles ab '#' ist die Kundenkennung der Teilrechnung und gehoert
          // nicht zur Belegnummer (siehe backfillReferenceFor).
          bookingReference.slice(BACKFILL_REFERENCE_PREFIX.length).split('#')[0].trim()
        : '';

/**
 * Alt-Deckung je Leistung aus dem Leistungsprotokoll.
 *
 * VERRECHNET, nicht bloss aufaddiert: eine negative ALT-Zeile ist die Korrektur
 * einer zu hoch nachgetragenen Menge und muss die Deckung mindern. Wuerde sie
 * uebergangen, meldete die Position dauerhaft mehr Deckung, als die
 * Alt-Rechnung gefordert hat — und maskierte damit spaeter erbrachte, offene
 * Leistung (dieselbe Fehlerklasse wie eine Deckung, die quantityBilled
 * ignoriert).
 *
 * Erst summieren, dann bei 0 kappen: das Ergebnis haengt damit nicht von der
 * Reihenfolge der Protokollzeilen ab. Eine Position, die auf 0 herauskommt,
 * faellt ganz heraus — sie deckt nichts mehr.
 */
export const altCoverageByService = (log: ServiceTransaction[]): Map<string, AltCoverage> => {
    const out = new Map<string, AltCoverage>();
    for (const t of log ?? []) {
        if (!isBackfillEntry(t)) continue;
        const quantity = t.quantity || 0;
        // Eine Zeile ohne Menge (auf 0 gekuerzt, siehe moveAltBackfill) ist nur
        // noch Beleg: sie traegt weder Menge noch Belegnummer bei.
        if (quantity === 0) continue;
        const found = out.get(t.serviceId) ?? { quantity: 0, invoiceNumbers: [] };
        found.quantity += quantity;
        const number = backfillInvoiceNumberOf(t.bookingReference);
        if (number && !found.invoiceNumbers.includes(number)) found.invoiceNumbers.push(number);
        out.set(t.serviceId, found);
    }
    for (const [id, coverage] of [...out]) {
        // Unter null gibt es keine Deckung — eine Korrektur, die weiter reicht
        // als der Nachtrag selbst, ist kein negativer Anspruch.
        coverage.quantity = Math.max(0, coverage.quantity);
        if (coverage.quantity === 0) out.delete(id);
        else coverage.invoiceNumbers.sort();
    }
    return out;
};

/** Nur die Mengen — die Form, die openByMonth erwartet. */
export const altCoveredQuantities = (log: ServiceTransaction[]): Map<string, number> =>
    new Map([...altCoverageByService(log)].map(([id, c]) => [id, c.quantity]));

/**
 * Die ids der ALT-Protokollzeilen.
 *
 * Fuer openByMonth: eine nachgetragene Zeile ist bezahlte Vergangenheit und
 * darf keinen Monat als "offen" belegen. Ohne diese Liste muesste die
 * Monatsverteilung die Alt-Menge blind von den AELTESTEN Eintraegen abziehen —
 * und traefe damit die falschen, sobald der Altbeleg spaeter datiert als
 * regulaer erfasste Leistung. Genau so verschwand ein Monat mit echten
 * Begehungen aus der Uebersicht, waehrend Vormonate offen erschienen.
 *
 * Als Set von aussen hereingereicht statt per Import: invoiceLogic ist die
 * Basisschicht und soll nicht auf dieses Modul zeigen.
 */
export const backfillEntryIds = (log: ServiceTransaction[]): Set<string> =>
    new Set((log ?? []).filter((t) => isBackfillEntry(t) && t.id).map((t) => t.id));

/**
 * Leistungen mit Alt-Buchung.
 *
 * Der Riegel gegen den Basisverlust: eine solche Position wurde bereits
 * gefordert, auch wenn quantityBilled 0 sagt. Sie aus einer kumulativen
 * Rechnung herauszunehmen zoege den Alt-Abschlag ohne die zugehoerige Leistung
 * ab (advance_exceeds_rate_basis).
 *
 * Aus DERSELBEN Verrechnung abgeleitet: nur wo netto Deckung uebrig ist, hat
 * die Alt-Rechnung etwas gefordert. Eine auf 0 gekuerzte oder wieder
 * herausgerechnete Position sperrte sonst dauerhaft ihren Ausschluss, ohne
 * irgendetwas zu decken.
 */
export const altBookedServiceIds = (log: ServiceTransaction[]): Set<string> =>
    new Set(altCoverageByService(log).keys());

// ═══════════════════════════════════════════════════════════════════════════
// Denselben Altbeleg noch einmal einlesen
// ═══════════════════════════════════════════════════════════════════════════

export interface BackfillRevertResult {
    services: ServiceItem[];
    serviceLog: ServiceTransaction[];
    /** Zurueckgenommene Menge je Position — leer, wenn nichts zu tun war. */
    revertedByService: Map<string, number>;
    /** Positionen, die dabei ganz entfallen sind (reiner Altbestand). */
    removedServiceIds: string[];
    /**
     * ids der Protokollzeilen, die aus dem serviceLog ENTFERNT wurden.
     *
     * Das Buchungsjournal kennt weder Update noch Delete: eine hier
     * weggenommene Zeile bliebe dort fuer immer stehen und zaehlte weiter mit.
     * Beim zweiten Einlesen desselben Altbelegs waere das die Doppelzaehlung —
     * die neuen Zeilen tragen frische uuids, der inhaltsabgeleitete
     * Dublettenschluessel greift also nicht. Der Aufrufer nimmt diese ids nach
     * dem bestaetigten Speichern mit `voidJournalEntries(..., 'backfill_revert')`
     * zurueck. Hier herausgegeben und nicht beim Aufrufer rekonstruiert: nur
     * diese Funktion weiss, welche Zeilen sie tatsaechlich gefiltert hat.
     *
     * SEIT MIGRATION Nr. 66 haben dieselben ids eine zweite Aufgabe, und sie
     * kommt ZUERST: `removeServiceLogEntries(projectId, removedEntryIds, …)`
     * nimmt die Zeilen SERVERSEITIG aus `projects.data`, bevor der Aufrufer
     * seinen Stand speichert. Der Riegel `service_log_guard_trg` wirft sonst —
     * er saehe die ALT-Zeilen in OLD stehen und in NEW fehlen. Die
     * Reihenfolge fuer jeden Aufrufer lautet damit: RPC, dann Save, dann Void
     * (Begruendung im Kopf von serviceLogRemoval.ts).
     */
    removedEntryIds: string[];
}

/**
 * Alles zuruecknehmen, was ein bestimmter Altbeleg ins Projekt geschrieben hat.
 *
 * Der zweite Import derselben Rechnung soll den ersten ERSETZEN, nicht neben
 * ihn treten: genau so entstanden 36 Stunden aus einer Rechnung ueber 18. Eine
 * blosse Warnung reicht dafuer nicht — sie laesst sich wegklicken, und die
 * Dublette faellt erst auf der naechsten Rechnung auf.
 *
 * Zurueckgerechnet wird die NETTOSUMME der Belegzeilen, nicht ihre Anzahl:
 * spaetere Korrekturen nach unten (negative ALT-Zeilen, siehe moveAltBackfill)
 * gehoeren zum selben Beleg und muessen mit. Sonst zoege die Ruecknahme mehr
 * ab, als der Beleg je hinzugefuegt hat.
 *
 * Gekappt bei 0: der Leistungsstand kann zwischenzeitlich von Hand nach unten
 * korrigiert worden sein. Eine Position darf daran nicht ins Negative kippen.
 *
 * Geloescht wird eine leergeraeumte Position nur, wenn sie nachweislich nichts
 * anderes traegt — dieselbe Regel wie in moveAltBackfill: keine weitere
 * Protokollzeile, keine verbliebene Abrechnungsmenge, Herkunft 'Altbestand'.
 *
 * keepServiceIds schuetzt davor, den Boden unter dem eigenen Fuss wegzuziehen:
 * beim erneuten Einlesen ist die leergeraeumte Position in aller Regel genau
 * das Ziel, auf das die neue Buchung gleich laufen soll. Erst loeschen und
 * dann darauf buchen liesse die Menge ins Leere gehen.
 *
 * Pur: der Aufrufer speichert.
 */
export const revertBackfill = (
    services: ServiceItem[],
    serviceLog: ServiceTransaction[],
    invoiceNumber: string,
    keepServiceIds: ReadonlySet<string> = new Set(),
    /**
     * Auftraggeber des Altbelegs — muss derselbe sein, mit dem eingelesen
     * wurde. Sonst trifft die Ruecknahme die Zeilen eines anderen Beteiligten
     * oder gar keine.
     */
    customerId?: string,
): BackfillRevertResult => {
    const reference = backfillReferenceFor(invoiceNumber, customerId);
    const log = serviceLog ?? [];
    // EIN Praedikat fuer beides: `affected` ist genau das, was `nextLog`
    // herauswirft. Zweimal hingeschrieben laufen die beiden irgendwann
    // auseinander, und dann voidet die Ruecknahme andere Zeilen, als das JSON
    // verliert.
    const istRuecknahmeZeile = (t: ServiceTransaction) =>
        isBackfillEntry(t) && t.bookingReference === reference;
    const affected = log.filter(istRuecknahmeZeile);

    if (!affected.length) {
        return {
            services,
            serviceLog: log,
            revertedByService: new Map(),
            removedServiceIds: [],
            removedEntryIds: [],
        };
    }

    // Zurueckgenommen wird, was das Einlesen TATSAECHLICH geschrieben hat.
    // Fehlen die Stempel (Zeilen von vor der Umstellung auf "anheben statt
    // addieren"), gilt die Belegmenge fuer beide Zaehler — damals wurde genau
    // sie addiert, fuer diese Zeilen ist es also weiterhin die exakte Umkehr.
    const revertedByService = new Map<string, number>();
    const revertUsed = new Map<string, number>();
    const revertBilled = new Map<string, number>();
    for (const t of affected) {
        const menge = t.quantity || 0;
        revertedByService.set(t.serviceId, (revertedByService.get(t.serviceId) ?? 0) + menge);
        revertUsed.set(
            t.serviceId,
            (revertUsed.get(t.serviceId) ?? 0) + (t.appliedUsed ?? menge),
        );
        revertBilled.set(
            t.serviceId,
            (revertBilled.get(t.serviceId) ?? 0) + (t.appliedBilled ?? menge),
        );
    }

    const nextLog = log.filter((t) => !istRuecknahmeZeile(t));

    const removedServiceIds: string[] = [];
    const nextServices = services
        .map((s) => {
            const back = revertedByService.get(s.id);
            if (back === undefined) return s;
            return {
                ...s,
                quantityUsed: Math.max(0, (s.quantityUsed || 0) - (revertUsed.get(s.id) ?? back)),
                quantityBilled: Math.max(0, (s.quantityBilled || 0) - (revertBilled.get(s.id) ?? back)),
            };
        })
        .filter((s) => {
            if (!revertedByService.has(s.id)) return true;
            const keep =
                keepServiceIds.has(s.id) ||
                nextLog.some((t) => t.serviceId === s.id) ||
                (s.quantityBilled || 0) > 0 ||
                s.category !== BACKFILL_CATEGORY;
            if (!keep) removedServiceIds.push(s.id);
            return keep;
        });

    // Genau die Zeilen, die `nextLog` oben herausgefiltert hat — dasselbe
    // Praedikat, nicht dieselbe Absicht zweimal aufgeschrieben.
    const removedEntryIds = affected
        .map((t) => (typeof t.id === 'string' ? t.id.trim() : ''))
        .filter(Boolean);
    // Eine entfernte Zeile OHNE id ist im Journal nicht adressierbar: sie
    // bleibt dort stehen, und niemand kann sie zurueckholen. Stillschweigend
    // wegzuwerfen waere die einzige wirklich stumme Stelle dieses Weges.
    const ohneId = affected.length - removedEntryIds.length;
    if (ohneId > 0) {
        console.warn(
            `[externalAdvances] ${ohneId} entfernte Protokollzeile(n) ohne id — im Journal nicht zurueckzunehmen (Beleg ${reference}).`,
        );
    }

    return {
        services: nextServices,
        serviceLog: nextLog,
        revertedByService,
        removedServiceIds,
        removedEntryIds,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// Zuordnung eines Nachtrags nachtraeglich korrigieren
// ═══════════════════════════════════════════════════════════════════════════

/**
 * same_service   — Quelle und Ziel sind dieselbe Position.
 * source_missing — die Quelle steht nicht (mehr) im LV.
 * target_missing — das Ziel steht nicht (mehr) im LV.
 * no_alt_entries — an der Quelle haengt keine Alt-Buchung; es gibt nichts zu
 *                  verschieben (und ohne diese Schranke wuerde eine normale
 *                  Leistung stillschweigend umgehaengt).
 * nothing_to_move— Alt-Buchungen ja, aber keine mit positiver Menge (nur
 *                  Korrekturen nach unten oder auf 0 gekuerzte Zeilen). Bewusst
 *                  ein eigener Grund: die Quelle SIEHT nach Nachtrag aus, und
 *                  "keine Alt-Buchung" waere an ihr schlicht die falsche
 *                  Auskunft.
 * ambiguous_entries — eine umziehende Alt-Buchung hat keine id, oder ihre id
 *                  steht mehr als einmal im Protokoll. Der Umzug erkennt seine
 *                  Zeilen an der id und naehme sonst fremde Zeilen mit.
 */
export type BackfillMoveError =
    | 'same_service'
    | 'source_missing'
    | 'target_missing'
    | 'no_alt_entries'
    | 'nothing_to_move'
    | 'ambiguous_entries';

export interface BackfillMoveResult {
    services: ServiceItem[];
    serviceLog: ServiceTransaction[];
    /** Summe der verschobenen ALT-Buchungen (die Protokollmenge). */
    movedQuantity: number;
    /**
     * Was davon im LV tatsaechlich umgebucht wurde: gedeckelt auf die erledigte
     * Menge der Quelle.
     *
     * Der Leistungsstand darf nach unten korrigiert worden sein — die
     * Protokollzeile bleibt dann als Beleg stehen, die Menge ist aber nicht mehr
     * da. Ohne den Deckel entstuenden beim Umhaengen Einheiten aus dem Nichts:
     * das Ziel bekaeme 5 dazu, die Quelle koennte nur 2 hergeben, und der
     * Leistungsstand des Projekts waere um 3 gewachsen.
     *
     * Die verschobenen Protokollzeilen werden auf diesen Wert mitgekuerzt (in
     * Protokollreihenfolge, erste Zeile zuerst, Rest auf 0) — sonst wiese die
     * Alt-Deckung des Ziels eine Menge aus, die dort nie angekommen ist.
     */
    appliedQuantity: number;
    /** Betroffene Alt-Belegnummern, fuer den Protokolltext. */
    invoiceNumbers: string[];
    /** id der geloeschten Alt-Position; fehlt, wenn die Quelle bestehen bleibt. */
    deletedServiceId?: string;
    /**
     * Warum die Quelle NICHT geloescht wurde:
     * other_entries — an ihr haengen weitere Protokollzeilen (echte Leistung),
     * billed        — an ihr steht eine Abrechnungsmenge,
     * not_backfill  — sie ist keine nachgetragene Altbestand-Position.
     */
    keptReason?: 'other_entries' | 'billed' | 'not_backfill';
    /**
     * ids der Protokollzeilen, die das Ziel gewechselt haben — in
     * Protokollreihenfolge.
     *
     * Fuer die Zweitschrift im Buchungsjournal: die Journalzeile traegt die
     * Position als Spalte, und ein Umzug, der nur den Blob schreibt, laesst
     * sie auf der Quelle stehen. Genau so zeigte das Journal von 6290-1 am
     * 02.09.2026 auf eine Altposition, die es nicht mehr gab, waehrend das
     * Protokoll die Zeile laengst auf 2.7.10 fuehrte (Drift-Sicht:
     * summe_weicht_ab, wert_weicht_ab). Der Aufrufer legt die Zeilen unter
     * der neuen Position an und legt die alten still.
     */
    movedEntryIds: string[];
}

/**
 * Alt-Buchungen einer Position auf eine andere umhaengen.
 *
 * Die Korrektur des Falles, den die Aehnlichkeitssuche verhindern soll, aber
 * nicht immer verhindert: der Nachtrag hat eine Dublette angelegt. Verschoben
 * werden die ALT-Protokollzeilen SAMT ihrer Menge — beides gehoert zusammen,
 * sonst behauptet das LV eine Menge, zu der kein Eintrag mehr steht.
 *
 * Die leergeraeumte Dublette wird nur geloescht, wenn sie nachweislich nichts
 * anderes traegt: keine weitere Protokollzeile, keine Abrechnungsmenge und die
 * Herkunft 'Altbestand'. Alles andere bleibt stehen — eine Position zu
 * loeschen, an der noch etwas haengt, ist der teurere Fehler.
 *
 * Pur: der Aufrufer speichert.
 */
export const moveAltBackfill = (
    services: ServiceItem[],
    serviceLog: ServiceTransaction[],
    sourceServiceId: string,
    targetServiceId: string,
): BackfillMoveResult | { error: BackfillMoveError } => {
    if (!sourceServiceId || sourceServiceId === targetServiceId) return { error: 'same_service' };
    const source = services.find((s) => s.id === sourceServiceId);
    if (!source) return { error: 'source_missing' };
    const target = services.find((s) => s.id === targetServiceId);
    if (!target) return { error: 'target_missing' };

    const log = serviceLog ?? [];
    const altEntries = log.filter((t) => t.serviceId === sourceServiceId && isBackfillEntry(t));
    if (!altEntries.length) return { error: 'no_alt_entries' };

    // Nur Zeilen mit positiver Menge ziehen um. Eine negative ALT-Zeile ist
    // eine Korrektur des Leistungsstands NACH UNTEN und gehoert zur Quelle: sie
    // mitzunehmen zoege dem Ziel Menge ab und schriebe dort eine negative
    // Buchung — und in der Summe liefe die Verschiebung sogar rueckwaerts.
    // Sie bleibt deshalb unveraendert an der Quelle stehen (und haelt diese
    // damit auch vor dem Loeschen zurueck: 'other_entries').
    const moved = altEntries.filter((t) => (t.quantity || 0) > 0);
    if (!moved.length) return { error: 'nothing_to_move' };

    // Jede umziehende Zeile braucht eine EIGENE id. Umgehaengt wird unten ueber
    // die id (`movedQuantityById.has(t.id)`): eine zweite Zeile mit derselben
    // id — auch eine fremde auf einer anderen Position — zoege mit aufs Ziel
    // und bekaeme die Alt-Menge, und ohne id wanderte JEDE id-lose Zeile des
    // Protokolls mit. Die Journal-Haelfte weist beides ab
    // (backfillMoveJournal.ts), aber dann ist das JSON schon falsch
    // gespeichert. Also gar nicht erst umziehen (Review 02.10.2026). Gezaehlt
    // wird beschnitten, wie im Journal: strenger als der exakte Vergleich der
    // Map, also faengt es jeden Fall, den sie verwechseln koennte.
    const kennungen = log.map((t) => String(t.id ?? '').trim());
    const mehrdeutig = moved.some((t) => {
        const k = String(t.id ?? '').trim();
        return !k || kennungen.filter((x) => x === k).length !== 1;
    });
    if (mehrdeutig) return { error: 'ambiguous_entries' };

    const movedQuantity = moved.reduce((sum, t) => sum + (t.quantity || 0), 0);
    // Deckel: mehr als die Quelle an erledigter Menge fuehrt, kann sie nicht
    // abgeben — sonst waechst der Leistungsstand des Projekts beim blossen
    // Umhaengen (siehe appliedQuantity).
    const appliedQuantity = Math.min(movedQuantity, Math.max(0, source.quantityUsed || 0));
    const invoiceNumbers = [
        ...new Set(moved.map((t) => backfillInvoiceNumberOf(t.bookingReference)).filter(Boolean)),
    ].sort();
    // Greift der Deckel, schrumpfen die Protokollzeilen MIT: aus ihnen rechnet
    // altCoverageByService die Alt-Deckung des Ziels. Blieben sie auf ihrer
    // alten Menge, meldete das Ziel mehr Deckung, als tatsaechlich umgezogen
    // ist — und verdeckte damit eigene offene Leistung.
    //
    // Verteilt wird in Protokollreihenfolge, die erste Zeile zuerst; was das
    // Budget uebersteigt, geht auf 0. Die Zeile bleibt als Beleg stehen (die
    // Belegnummer ist die Herkunft), zaehlt aber nirgends mehr mit.
    const movedQuantityById = new Map<string, number>();
    let budget = appliedQuantity;
    for (const t of moved) {
        const take = Math.min(Math.max(0, t.quantity || 0), budget);
        movedQuantityById.set(t.id, take);
        budget -= take;
    }
    const nextLog = log.map((t) =>
        movedQuantityById.has(t.id)
            ? { ...t, serviceId: targetServiceId, quantity: movedQuantityById.get(t.id) ?? 0 }
            : t,
    );

    // Die abgerechnete Menge zieht in derselben Hoehe mit wie die erledigte.
    // Der Nachtrag hat BEIDE gesetzt (applyBackfillIncrements) — bliebe
    // quantityBilled an der Quelle stehen, stuende die laengst geforderte Menge
    // am Ziel erneut als offen und liefe ein zweites Mal in eine Rechnung,
    // waehrend die Quelle mit billed > used zurueckbliebe und den Projektcheck
    // dauerhaft rot faerbte (docs/audit-befunde/block-E.md, Befund E-11).
    //
    // Gedeckelt auf das, was die Quelle wirklich traegt: nach einem Storno kann
    // dort weniger abgerechnete Menge stehen als erledigte, und aus dem Nichts
    // darf beim Umhaengen keine entstehen.
    const billedQuantity = Math.min(appliedQuantity, Math.max(0, source.quantityBilled || 0));

    const remainingEntries = nextLog.some((t) => t.serviceId === sourceServiceId);
    // Bewusst der Stand VOR dem Umzug: eine Position, auf der je eine
    // abgerechnete Menge stand, bleibt stehen, auch wenn sie jetzt leer ist.
    // Sie stattdessen zu loeschen waere die naheliegende Ergaenzung, wuerde
    // aber die Loeschregel ausweiten — und die prueft nicht, ob eine
    // festgeschriebene Rechnungszeile auf die Position zeigt (block-E.md,
    // Befund E-12-01, offen). Eine leer zurueckbleibende Dublette ist
    // kosmetisch; ein Beleg, der ins Leere zeigt, ist es nicht.
    const billed = source.quantityBilled || 0;
    let keptReason: BackfillMoveResult['keptReason'];
    if (remainingEntries) keptReason = 'other_entries';
    else if (billed > 0) keptReason = 'billed';
    else if (source.category !== BACKFILL_CATEGORY) keptReason = 'not_backfill';

    // Was an der Quelle an eigenem Protokoll ZURUECKBLEIBT. Untergrenze fuer
    // ihren Leistungsstand — und der Grund, warum hier nicht blind subtrahiert
    // werden darf:
    //
    // Hat der Import die Quelle nur ANGEHOBEN (applyBackfillIncrements, eine
    // bestehende Position mit eigener Leistung), steckt in ihrem quantityUsed
    // nicht nur die Alt-Menge, sondern auch echte protokollierte Leistung. Der
    // volle Abzug nimmt beides. Genau so verschwand in Projekt 6105 die
    // anlassbezogene Begehung vom 30.07.: die Position stand nach dem Import
    // auf 4 (Protokoll 1, Alt-Rechnung 4), der Umzug zog 4 ab — und die eine
    // echte Begehung war weg, ohne dass ihr Protokolleintrag verschwand.
    const sourceRest = Math.max(
        0,
        nextLog
            .filter((t) => t.serviceId === sourceServiceId)
            .reduce((sum, t) => sum + (t.quantity || 0), 0),
    );

    // Leistungsdatum des umziehenden Belegs: der spaeteste Tag seiner
    // Alt-Zeilen (eine Belegnummer, ein Datum — bei mehreren der spaeteste,
    // damit im Zweifel weniger „danach" zaehlt und nichts doppelt).
    const altServiceDate = moved
        .filter((t) => buchungstag(t.date))
        .map((t) => String(t.date))
        .sort((x, y) => String(buchungstag(x)).localeCompare(String(buchungstag(y))))
        .pop();

    const nextServices = services
        .map((s) => {
            if (s.id === sourceServiceId) {
                return {
                    ...s,
                    quantityUsed: Math.max(sourceRest, (s.quantityUsed || 0) - appliedQuantity),
                    quantityBilled: Math.max(0, (s.quantityBilled || 0) - billedQuantity),
                };
            }
            if (s.id === targetServiceId) {
                // ANHEBEN, NICHT ADDIEREN — dieselbe Regel wie beim Einlesen
                // (applyBackfillIncrements): eine Alt-Rechnung sagt, welcher
                // STAND dieser Position bereits abgerechnet ist, nicht welcher
                // Zuwachs dazukommt. Die Zuordnung nachtraeglich zu korrigieren
                // muss zum selben Stand fuehren, als waere gleich richtig
                // zugeordnet worden.
                //
                // Addiert stand Projekt 6105 nach dem Umzug auf 26 erbrachten
                // Begehungen bei 22 protokollierten und 22 beauftragten: die 4
                // der Alt-Rechnung sind Teil der 22, nicht zusaetzlich zu ihnen.
                //
                // appliedQuantity geht in die Untergrenze der erledigten Menge
                // ein, damit eine Quelle ohne Abrechnungsmenge (eine vom
                // Import angelegte Dublette aelterer Bauart) ihre Menge nicht
                // verliert: der Beleg weist sie als erbracht aus.
                //
                // DIESELBE ZEITREGEL WIE BEIM EINLESEN (nextUsed): setzt der
                // Umzug den Abrechnungsstand des Ziels, kommt die Leistung NACH
                // dem Datum der umziehenden Alt-Zeilen oben drauf — sie kann
                // nicht im Beleg stecken. Vorfall 5633 (29.09.2026): ALT-5633/12
                // (25,5 Monate bis Juni) ging von einer Altbestand-Position auf
                // die Monatspauschale, die schon 07/2026 und 08/2026 gebucht
                // hatte (2 / 0). Angehoben stand sie auf 25,5 / 25,5 — Juli und
                // August waren damit als abgerechnet verbucht und nicht mehr
                // forderbar. Mit der Zeitregel: 25,5 + 2 = 27,5 / 25,5.
                // 6105 bleibt bei 22: dort liegen nur 18 der 22 Begehungen nach
                // dem Beleg, 4 + 18 hebt nicht ueber den Stand.
                const quantityBilled = Math.max(s.quantityBilled || 0, billedQuantity);
                const setztStand = billedQuantity > (s.quantityBilled || 0) + QUANTITY_TOLERANCE;
                // Geteiltes Projekt (Alt-Zeilen mit Auftraggeber): wie beim
                // Einlesen (nextUsed, `context`) keine Zeitregel — dort deckt
                // jeder Beleg nur den Anteil seines Auftraggebers, die Leistung
                // danach kann ein spaeterer Beleg eines anderen gedeckt haben.
                const geteilt = moved.some((t) => !!t.splitPartyId || !!t.customerId);
                const danach = setztStand && !geteilt && altServiceDate
                    ? performedAfterServiceDate(nextLog, targetServiceId, altServiceDate)
                    : 0;
                return {
                    ...s,
                    quantityUsed: Math.max(
                        s.quantityUsed || 0,
                        quantityBilled,
                        appliedQuantity,
                        Math.round((quantityBilled + danach) * 1000) / 1000,
                    ),
                    quantityBilled,
                };
            }
            return s;
        })
        .filter((s) => keptReason !== undefined || s.id !== sourceServiceId);

    return {
        services: nextServices,
        serviceLog: nextLog,
        movedQuantity,
        appliedQuantity,
        invoiceNumbers,
        deletedServiceId: keptReason === undefined ? sourceServiceId : undefined,
        keptReason,
        movedEntryIds: moved.map((t) => String(t.id)),
    };
};
